<template>
  <div id="my-bets-component" :class="{ 'my-bets--sheet': sheetMode }">

    <div v-if="overlay" class="global-overlay">
      <div class="spinner-lg"></div>
    </div>

    <div v-if="getAuthenticated">

      <div v-if="loading" class="skeleton-list">
        <div class="skel-bet" v-for="n in 5" :key="n">
          <div class="skel-row">
            <div class="skel-blob" style="width:38%"></div>
            <div class="skel-blob" style="width:22%"></div>
          </div>
          <div class="skel-row" style="margin-top:6px">
            <div class="skel-blob" style="width:28%"></div>
            <div class="skel-blob" style="width:18%"></div>
            <div class="skel-dot"></div>
          </div>
        </div>
      </div>

      <template v-else>
        <div v-if="_visibleBets().length > 0" class="section-header">
          <span class="section-label">{{ sheetMode ? $t('cartSheet.recentBets') : $t('lastBets') }}</span>
        </div>

        <div v-else class="mb-empty">
          <div class="mb-empty__art">
            <svg viewBox="0 0 120 96" width="120" height="96" fill="none" stroke-linecap="round" stroke-linejoin="round">
              <g stroke="var(--color-primary-light)" stroke-width="3">
                <path d="M16 36l9 5M14 52h10M16 68l9-5"/>
                <path d="M104 36l-9 5M106 52H96M104 68l-9-5"/>
              </g>
              <g stroke="var(--text-op-50-platform)" stroke-width="3">
                <rect x="38" y="16" width="44" height="68" rx="6"/>
                <rect x="50" y="10" width="20" height="12" rx="3"/>
                <path d="M48 40h24M48 52h24M48 64h14"/>
              </g>
            </svg>
          </div>
          <span class="mb-empty__title">{{ $t('noMyBets') }}</span>
          <span class="mb-empty__sub">{{ $t('noMyBetsHint') || 'Le tue scommesse appariranno qui' }}</span>
          <button v-if="sheetMode" class="mb-empty__cta" @click="$root.$emit('close_sidebar_right', true)">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><circle cx="11" cy="11" r="7"/><path d="M20 20l-3.5-3.5"/></svg>
            <span>{{ $t('cartSheet.browseMatches') }}</span>
          </button>
          <button v-if="!cashoutOnly" class="mb-empty__link" @click="allMyBets()">{{ $t('cartSheet.viewHistory') }}</button>
        </div>

        <div
          v-for="(bet, index) in _visibleBets()"
          :key="bet.code || bet.betCode || index"
          :class="['single-bet', `single-bet--${bet.result}`]"
          @click="openDetail(bet)"
          :title="$t('viewBetDetail')"
        >
          <div class="bet-info">
            <div class="bet-row bet-row--head">
              <div class="bet-type">
                <span class="bet-type-name">{{ $t(bet.type) }}</span>
                <span v-if="bet.odds && bet.odds.length" class="bet-event-count">({{ bet.odds.length }})</span>
                <span class="bet-date">· {{ formatBetDate(bet.created_at) }}</span>
                <span v-if="bet.bet_context === 'live'" class="icon-live" :title="$t('live')" :aria-label="$t('live')"><v-icon x-small>mdi-access-point</v-icon></span>
                <span v-if="bet.betFreeze" class="badge-bet-freeze" :class="{ 'badge-bet-freeze--overridden': bet.betFreezeOverridden }" :title="bet.betFreezeOverridden ? $t('betFreeze.overridden') : $t('betFreeze.badge')">&#10052;</span>
              </div>
              <span v-if="bet.cashout" class="badge-cashout">{{ $t('ritira') }}</span>
              <span v-else class="bet-status-badge" :class="`bet-status-badge--${bet.result}`">{{ $t('bets.'+bet.result) }}</span>
            </div>

            <div class="bet-row bet-row--meta">
              <span class="bet-money">
                <span class="bet-money-label">{{ $t('amount') }}:</span>
                <span class="bet-money-amount">{{ parseFloat(bet.amount_real).toFixed(2) }} {{ bet.currency }}</span>
              </span>
              <span class="bet-money">
                <span class="bet-money-label">{{ $t('winning') }}:</span>
                <span
                    class="bet-money-win"
                    :class="`bet-money-win--${bet.result}`"
                    :title="bet.winning_real ? $t('winning') : $t('potentialWinnings')"
                >{{ bet.winning_real ? parseFloat(bet.winning_real).toFixed(2) + ' ' + bet.currency : getWinning(bet) }}</span>
              </span>
            </div>
            <div v-if="!bet.winning_real && betFreezeOriginalWinning(bet)" class="bet-row bet-row--freeze-winning">
              <span class="bet-freeze-before">&#10052; {{ $t('betFreeze.beforeFreeze') }}</span>
              <s class="bet-freeze-before-value">{{ betFreezeOriginalWinning(bet) }}</s>
            </div>

            <!-- Bet builder: no cashout (server rule), say why instead of a missing button -->
            <div v-if="showBetBuilderCashoutNote(bet)" class="bet-row bet-row--cashout cashout-bb-note" @click.stop>
              {{ $t('cashout.betBuilderNotAvailable') }}
            </div>
            <div v-if="bet.result === 'pending' && isCashoutEligible(bet) && isCashoutVisible(bet)"
                 class="bet-row bet-row--cashout" @click.stop>
              <template v-if="getCashoutByCode(bet.code).status === 'available'">
                <button class="cashout-btn cashout-btn--hero" @click.stop="openCashoutConfirm(bet, getCashoutByCode(bet.code))">
                  <span class="cashout-btn__label">
                    <v-icon small class="cashout-btn__icon">mdi-cash-fast</v-icon>
                    {{ $t('cartSheet.cashoutNow') }}
                    <span v-if="getCashoutByCode(bet.code).hasActiveLiveEvents" class="cashout-btn__live" title="LIVE"></span>
                  </span>
                  <span class="cashout-btn__amount">{{ formatCashoutAmount(bet) }} <small>{{ bet.currency }}</small></span>
                </button>
              </template>
              <template v-else-if="getCashoutByCode(bet.code).status === 'suspended'">
                <button class="cashout-btn cashout-btn--suspended" disabled :title="cashoutReasonText(getCashoutByCode(bet.code), true)">
                  {{ $t('ritira') }} <span class="cashout-btn-reason">· {{ cashoutReasonText(getCashoutByCode(bet.code)) }}</span>
                </button>
              </template>
              <template v-else-if="getCashoutByCode(bet.code).status === 'temporary'">
                <button class="cashout-btn cashout-btn--temporary" disabled :title="cashoutReasonText(getCashoutByCode(bet.code), true)">
                  {{ $t('ritira') }} <span class="cashout-btn-reason">· {{ cashoutReasonText(getCashoutByCode(bet.code)) }}</span>
                </button>
              </template>
              <template v-else-if="getCashoutByCode(bet.code).status === 'loading'">
                <button class="cashout-btn cashout-btn--suspended" disabled :title="$t('cashout.temporarilyUnavailable')">
                  <div class="spin-sm"></div>
                  {{ $t('ritira') }}
                </button>
              </template>
            </div>

            <div v-if="cashoutConfirmBet && cashoutConfirmBet.code === bet.code && !cashoutModalInlineOpen"
                 class="cashout-inline-panel" @click.stop>
              <template v-if="cashoutInlineStep === 'confirm'">
                <div v-if="cashoutFrozenExpired" class="cashout-expired-notice">
                  {{ $t('cashout.expired') }}
                  <div v-if="cashoutConfirmData && cashoutConfirmData.status === 'available'" class="mt-2 font-weight-bold cashout-confirm-amount">
                    {{ cashoutAmountForConfirm != null ? cashoutAmountForConfirm.toFixed(2) : '' }}
                    {{ cashoutConfirmBet.currency }}
                  </div>
                </div>
                <div v-else class="cashout-confirm-body">
                  {{ $t('cashout.confirmMsg') }}
                  <strong class="cashout-confirm-amount">
                    {{ cashoutAmountForConfirm != null ? cashoutAmountForConfirm.toFixed(2) : '' }}
                    {{ cashoutConfirmBet.currency }}
                  </strong>
                  <div v-if="cashoutConfirmData && cashoutConfirmData.hasActiveLiveEvents" class="mt-2">
                    <span class="cashout-live-badge">LIVE</span>
                  </div>
                  <div v-if="cashoutConfirmData && cashoutConfirmData.hasSettledWonEvents && !cashoutConfirmData.hasActiveLiveEvents"
                       class="mt-2 text-caption cashout-partial-won">
                    {{ $t('cashout.partialWon') }}
                  </div>
                </div>
                <div class="cashout-inline-actions">
                  <button class="cashout-confirm-btn" :class="{ 'is-loading': cashoutExecuting }" :disabled="cashoutExecuting" @click.stop="confirmCashout">
                    <span v-if="!cashoutExecuting">{{ cashoutFrozenExpired ? $t('cashout.retry') : $t('Agree') }}</span>
                    <span v-else class="btn-spinner-inline"></span>
                  </button>
                  <button class="dialog-close-btn" @click.stop="closeCashoutInline" :disabled="cashoutExecuting">{{ $t('removeBet') }}</button>
                </div>
              </template>
              <template v-if="cashoutInlineStep === 'double'">
                <div v-if="cashoutBypassLoading" class="cashout-verifying">
                </div>
                <div v-else-if="cashoutFreshData" class="cashout-confirm-body">
                  {{ $t('cashout.finalConfirmMsg') }}
                  <strong class="cashout-confirm-amount">
                    {{ cashoutFreshData.amount != null ? cashoutFreshData.amount.toFixed(2) : '' }}
                    {{ cashoutConfirmBet.currency }}
                  </strong>
                  <div v-if="cashoutFreshData.hasActiveLiveEvents" class="mt-2">
                    <span class="cashout-live-badge">{{ $t('live') }}</span>
                  </div>
                  <div v-if="cashoutFreshData.hasSettledWonEvents && !cashoutFreshData.hasActiveLiveEvents"
                       class="mt-2 text-caption cashout-partial-won">
                    {{ $t('cashout.partialWon') }}
                  </div>
                </div>
                <div class="cashout-inline-actions">
                  <button class="dialog-close-btn" @click.stop="closeCashoutInline" :disabled="cashoutBypassLoading || cashoutExecuting" v-if="!cashoutBypassLoading && cashoutFreshData">{{ $t('removeBet') }}</button>
                  <button v-if="!cashoutBypassLoading && cashoutFreshData"
                          class="cashout-confirm-btn" :class="{ 'is-loading': cashoutExecuting }" :disabled="cashoutExecuting"
                          @click.stop="proceedCashout">
                    <span v-if="!cashoutExecuting">
                      <svg class="btn-hourglass" viewBox="0 0 24 24" fill="currentColor" width="13" height="13"><path d="M6 2v6l4 4-4 4v6h12v-6l-4-4 4-4V2H6zm10 14.5V20H8v-3.5l4-4 4 4zm-4-5-4-4V4h8v3.5l-4 4z"/></svg>
                      {{ $t('cashout.proceed') }}
                    </span>
                    <span v-else class="btn-spinner-inline"></span>
                  </button>
                </div>
              </template>
            </div>

            <!-- betFreeze teaser: only when the server says a leg can be frozen -->
            <div v-if="betFreezeTeaser(bet)" class="bet-row bet-row--freeze" @click.stop>
              <button class="cashout-btn cashout-btn--hero freeze-hero" :title="$t('betFreeze.teaser.cta')" @click.stop="openDetail(bet)">
                <span class="cashout-btn__label">
                  <v-icon small class="cashout-btn__icon">mdi-snowflake</v-icon>
                  {{ $t('betFreeze.teaser.text') }}
                </span>
                <span class="cashout-btn__amount freeze-hero__go"><v-icon small>mdi-chevron-right</v-icon></span>
              </button>
            </div>
          </div>
        </div>

        <div class="load-more-row" @click="allMyBets()" v-if="getMyBets.length > 0">
          <span class="primary-button" :title="$t('loadmore')">
            {{ $t('loadmore') }}
            <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14">
              <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd"/>
            </svg>
          </span>
        </div>
      </template>
    </div>

    <div v-else class="mb-empty">
      <!-- Non loggato: spiega cosa si ottiene e porta al login -->
      <div class="mb-empty__art">
        <svg viewBox="0 0 120 96" width="120" height="96" fill="none" stroke-linecap="round" stroke-linejoin="round">
          <g stroke="var(--text-op-50-platform)" stroke-width="3">
            <rect x="30" y="14" width="44" height="68" rx="6"/>
            <path d="M40 32h24M40 44h24M40 56h14"/>
          </g>
          <g stroke="var(--color-primary-light)" stroke-width="3">
            <rect x="62" y="52" width="30" height="24" rx="5" fill="var(--bg-body)"/>
            <path d="M68 52v-6a9 9 0 0 1 18 0v6"/>
            <path d="M77 62v5"/>
          </g>
        </svg>
      </div>
      <span class="mb-empty__title">{{ $t('cartSheet.loginTitle') }}</span>
      <span class="mb-empty__sub">{{ $t('cartSheet.loginHint') }}</span>
      <button class="mb-empty__cta" @click="requestLogin">
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><path d="M10 17l5-5-5-5M15 12H3"/></svg>
        <span>{{ $t('cartSheet.login') }}</span>
      </button>
    </div>


    <!-- Dialog di dettaglio/lista spostati nella root dell'app al mount (vedi
         mountDialogLayer): dentro il carrello mobile verrebbero ritagliati e
         coperti dall'header della modale, soprattutto su Safari iOS. -->
    <div ref="dialogLayer" class="td-dialog-layer">
    <transition name="td-backdrop-fade">
      <div v-if="dialog && !getModalIdle" class="td-overlay-backdrop" @click.self="closeDetail(true)">
        <transition name="td-sheet">
          <div v-if="dialog" class="td-card" @click.stop>
        <!-- Loading overlay -->
        <div v-if="overlay_dialog_betlist_detail" class="td-overlay">
          <div class="spinner-lg"></div>
        </div>

        <!-- Top bar -->
        <div class="td-topbar">
          <button v-if="detailFromAllBets" class="td-icon-btn td-back-btn" :aria-label="$t('cartSheet.backToAllBets')" :title="$t('cartSheet.backToAllBets')" @click="closeDetail(true)">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
          </button>
          <div class="td-code-block">
            <span class="td-code">{{ getShortCode ? betToShow.betCode : betToShow.code }}</span>
            <template v-if="getShortCode && betToShow.code">
              <button class="td-icon-btn" @click.stop="showFullCode = !showFullCode" :title="$t('cashout.revealHint')">
                <svg v-if="showFullCode" viewBox="0 0 24 24" fill="currentColor" width="14" height="14"><path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"/></svg>
                <svg v-else viewBox="0 0 24 24" fill="currentColor" width="14" height="14"><path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/></svg>
              </button>
              <span v-if="showFullCode" class="td-code-full">{{ betToShow.code }}</span>
            </template>
          </div>
          <div class="td-topbar-right">
            <button v-if="betToShow.result === 'pending'" class="td-icon-btn" @click="rebet(betToShow.odds)" :title="$t('rebet')">
              <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16"><path d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z"/></svg>
            </button>
            <button class="td-icon-btn" @click="printBet(getShortCode ? betToShow.betCode : betToShow.code)" :title="$t('print')">
              <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16"><path d="M19 8H5c-1.66 0-3 1.34-3 3v6h4v4h12v-4h4v-6c0-1.66-1.34-3-3-3zm-3 11H8v-5h8v5zm3-7c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm-1-9H6v4h12V3z"/></svg>
            </button>
            <button class="td-icon-btn" @click="download(getShortCode ? betToShow.betCode : betToShow.code)" :title="$t('download')">
              <v-icon small>mdi-download</v-icon>
            </button>
            <button class="td-icon-btn" @click="onShareBetImage" :title="$t('shareImage')" :class="{ 'loading-btn': loadingShareImage }">
              <v-progress-circular v-if="loadingShareImage" indeterminate size="14" width="2" color="rgba(255,255,255,0.55)"></v-progress-circular>
              <v-icon v-else>mdi-export-variant</v-icon>
            </button>
            <button class="td-icon-btn" :aria-label="$t('close')" @click="closeDetail(false)">
              <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
            </button>
          </div>
        </div>

        <div class="td-scroll-body">

        <!-- ── Riepilogo: stato, importi e cashout in evidenza ── -->
        <section class="td-hero" :class="`td-hero--${betToShow.result}`">
          <div class="td-hero-head">
            <div class="td-hero-title">
              <span class="td-type">{{ $t(betToShow.type) }}</span>
              <span class="td-hero-count">{{ detailEventsCount }} {{ $t('bets.events') }}</span>
              <span v-if="betToShow.bet_context === 'live'" class="badge-live">{{ $t('live') }}</span>
              <span v-else-if="betToShow.bet_context === 'antepost'" class="ctx-badge ctx-badge--antepost">ANTEPOST</span>
              <span v-if="betToShow.betFreeze" class="badge-bet-freeze" :class="{ 'badge-bet-freeze--overridden': betToShow.betFreezeOverridden }" :title="betToShow.betFreezeOverridden ? $t('betFreeze.overridden') : $t('betFreeze.badge')">&#10052;</span>
              <span v-if="isFreeBetBet(betToShow)" class="badge-freebet" :title="betToShow.freeBetTitle || $t('freeBets.badge')">
                <v-icon x-small>mdi-gift</v-icon>{{ betToShow.freeBetTitle || $t('freeBets.badge') }}
              </span>
            </div>
            <span class="td-result-badge" :class="`td-result-badge--${betToShow.result}`">{{ $t('bets.'+betToShow.result) }}</span>
          </div>
          <div class="td-hero-date">
            {{ new Date(betToShow.created_at).toLocaleString() }}
            <span v-if="betToShow.cashout" class="badge-cashout">{{ $t('ritira') }}</span>
          </div>

          <div class="td-hero-stats">
            <div class="td-stat">
              <span class="td-stat-label">{{ $t('amount') }}</span>
              <span class="td-stat-value">{{ parseFloat(betToShow.amount_real).toFixed(2) }} <small>{{ betToShow.currency }}</small></span>
            </div>
            <div class="td-stat td-stat--accent" v-if="betToShow.result === 'pending'">
              <span class="td-stat-label">{{ $t('potentialWinnings') }}</span>
              <span class="td-stat-value">{{ getWinning(betToShow) }}</span>
              <span v-if="betFreezeOriginalWinning(betToShow)" class="bet-freeze-before td-stat-sub"><template v-if="betToShow.betFreeze">&#10052; </template>{{ originalWinningLabel(betToShow) }} <s>{{ betFreezeOriginalWinning(betToShow) }}</s></span>
            </div>
            <div class="td-stat" v-else :class="{ 'td-stat--accent': betToShow.result === 'win' }">
              <span class="td-stat-label">{{ $t('winning') }}</span>
              <span class="td-stat-value">
                {{ betToShow.winning_real ? parseFloat(betToShow.winning_real).toFixed(2) : '0.00' }} <small>{{ betToShow.currency }}</small>
              </span>
            </div>
          </div>

          <div class="td-hero-rows">
            <div class="td-hero-row" v-if="isFreeBetBet(betToShow) && betToShow.freeBetMaxWin != null">
              <span>{{ $t('freeBets.maxWinLabel') }}</span>
              <span>{{ parseFloat(betToShow.freeBetMaxWin).toFixed(2) }} {{ betToShow.currency }}</span>
            </div>
            <div class="td-hero-row">
              <span>{{ $t('bonus') }}</span>
              <!-- single / multiple: bonus that applies today (a void or frozen leg can lower the tier), placement one struck -->
              <span v-if="bonusDisplay(betToShow)"><s v-if="bonusDisplay(betToShow).original" class="td-bonus-before">{{ bonusDisplay(betToShow).original }}</s>{{ bonusDisplay(betToShow).current }}</span>
              <span v-else>{{ getBonus(betToShow) }}</span>
            </div>
            <div class="td-hero-row" v-if="betToShow.result !== 'pending'">
              <span>{{ $t('potentialWinnings') }}</span>
              <span>{{ getWinning(betToShow) }}</span>
            </div>
            <div class="td-hero-row" v-if="isTaxedBet(betToShow) && !betToShow.betFreeze && !hasRecalculatedFigures(betToShow)">
              <span>{{ $t('expected_winning_total_taxed') }}</span>
              <span>{{ getWinningTaxed(betToShow) }}</span>
            </div>
          </div>

          <div v-if="betFreezeNotice" class="td-freeze-notice" :class="`td-freeze-notice--${betFreezeNotice.type}`">
            &#10052; {{ betFreezeNotice.text }}
          </div>

          <!-- Cashout: azione principale, subito sotto agli importi -->
          <div v-if="showBetBuilderCashoutNote(betToShow)" class="td-hero-cashout cashout-bb-note">{{ $t('cashout.betBuilderNotAvailable') }}</div>
          <div class="td-hero-cashout" v-if="isPendingAndCashoutEligible && isCashoutVisible(betToShow)">
            <div class="td-cashout-area">
              <!-- Cashout button -->
              <template v-if="!cashoutModalInlineOpen">
                <button v-if="getCashoutByCode(betToShow.code).status === 'available'"
                  class="cashout-btn cashout-btn--hero td-cashout-btn"
                  @click="openModalCashout(betToShow, getCashoutByCode(betToShow.code))">
                  <span class="cashout-btn__label">
                    <v-icon class="cashout-btn__icon">mdi-cash-fast</v-icon>
                    {{ $t('cartSheet.cashoutNow') }}
                    <span v-if="getCashoutByCode(betToShow.code).hasActiveLiveEvents" class="cashout-btn__live" title="LIVE"></span>
                  </span>
                  <span class="cashout-btn__amount">{{ formatCashoutAmount(betToShow) }} <small>{{ betToShow.currency }}</small></span>
                </button>
                <template v-else-if="getCashoutByCode(betToShow.code).status === 'suspended' || getCashoutByCode(betToShow.code).status === 'temporary'">
                  <button class="cashout-btn cashout-btn--suspended td-cashout-btn" disabled :title="cashoutReasonText(getCashoutByCode(betToShow.code), true)">
                    {{ $t('ritira') }} <span class="cashout-btn-reason">· {{ cashoutReasonText(getCashoutByCode(betToShow.code)) }}</span>
                  </button>
                  <div v-if="getCashoutByCode(betToShow.code).suspendedEventName" class="cashout-reason">{{ getCashoutByCode(betToShow.code).suspendedEventName }}</div>
                </template>
              </template>

              <!-- Inline cashout panel (step 1: confirm) -->
              <div v-if="cashoutModalInlineOpen && cashoutInlineStep === 'confirm'" class="td-cashout-panel">
                <div v-if="cashoutFrozenExpired" class="cashout-expired-notice">
                  {{ $t('cashout.expired') }}
                  <div v-if="cashoutConfirmData && cashoutConfirmData.status === 'available'" class="mt-2 font-weight-bold cashout-confirm-amount">
                    {{ cashoutAmountForConfirm != null ? cashoutAmountForConfirm.toFixed(2) : '' }}
                    {{ cashoutConfirmBet && cashoutConfirmBet.currency }}
                  </div>
                </div>
                <div v-else class="cashout-confirm-body">
                  {{ $t('cashout.confirmMsg') }}
                  <strong class="cashout-confirm-amount">
                    {{ cashoutAmountForConfirm != null ? cashoutAmountForConfirm.toFixed(2) : '' }}
                    {{ cashoutConfirmBet && cashoutConfirmBet.currency }}
                  </strong>
                  <div v-if="cashoutConfirmData && cashoutConfirmData.hasActiveLiveEvents" class="mt-2">
                    <span class="cashout-live-badge">LIVE</span>
                  </div>
                </div>
                <div class="cashout-inline-actions">
                  <button class="dialog-close-btn" @click="closeCashoutInline" :disabled="cashoutExecuting">{{ $t('removeBet') }}</button>
                  <button class="cashout-confirm-btn" :class="{ 'is-loading': cashoutExecuting }" :disabled="cashoutExecuting" @click="confirmCashout">
                    <span v-if="!cashoutExecuting">{{ cashoutFrozenExpired ? $t('cashout.retry') : $t('Agree') }}</span>
                    <span v-else class="btn-spinner-inline"></span>
                  </button>
                </div>
              </div>

              <!-- Inline cashout panel (step 2: double confirm) -->
              <div v-if="cashoutModalInlineOpen && cashoutInlineStep === 'double'" class="td-cashout-panel">
                <div v-if="cashoutBypassLoading" class="cashout-verifying">
                  <div class="spin-sm" style="margin-right:10px;flex-shrink:0"></div>
                  <span>{{ $t('cashout.verifying') }}</span>
                </div>
                <div v-else-if="cashoutFreshData" class="cashout-confirm-body">
                  {{ $t('cashout.finalConfirmMsg') }}
                  <strong class="cashout-confirm-amount">
                    {{ cashoutFreshData.amount != null ? cashoutFreshData.amount.toFixed(2) : '' }}
                    {{ cashoutConfirmBet && cashoutConfirmBet.currency }}
                  </strong>
                  <div v-if="cashoutFreshData.hasActiveLiveEvents" class="mt-2">
                    <span class="cashout-live-badge">LIVE</span>
                  </div>
                </div>
                <div class="cashout-inline-actions">
                  <button class="dialog-close-btn" @click="closeCashoutInline" :disabled="cashoutBypassLoading || cashoutExecuting" v-if="!cashoutBypassLoading && cashoutFreshData">{{ $t('removeBet') }}</button>
                  <button v-if="!cashoutBypassLoading && cashoutFreshData"
                    class="cashout-confirm-btn" :class="{ 'is-loading': cashoutExecuting }" :disabled="cashoutExecuting"
                    @click="proceedCashout">
                    <span v-if="!cashoutExecuting">
                      <svg class="btn-hourglass" viewBox="0 0 24 24" fill="currentColor" width="13" height="13"><path d="M6 2v6l4 4-4 4v6h12v-6l-4-4 4-4V2H6zm10 14.5V20H8v-3.5l4-4 4 4zm-4-5-4-4V4h8v3.5l-4 4z"/></svg>
                      {{ $t('cashout.proceed') }}
                    </span>
                    <span v-else class="btn-spinner-inline"></span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- ── Eventi ── -->
        <section class="td-events">
          <div class="td-events-head">
            <span class="td-events-title">{{ $t('bets.events') }} <span class="td-events-count">{{ detailEventsCount }}</span></span>
            <button class="td-toggle-btn" @click="detailLegsExpanded = !detailLegsExpanded">
              {{ detailLegsExpanded ? $t('hide') : $t('show') }}
              <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14" :style="detailLegsExpanded ? 'transform:rotate(180deg)' : ''"><path d="M7 10l5 5 5-5z"/></svg>
            </button>
          </div>

          <!-- betFreeze available: how many selections can still be frozen -->
          <div v-if="betFreezeAvailableCount > 0" class="td-freeze-available">
            <v-icon small class="td-freeze-available-icon">mdi-snowflake</v-icon>
            <span>{{ $tc('betFreeze.availableCount', betFreezeAvailableCount, {n: betFreezeAvailableCount}) }}</span>
          </div>

          <div class="td-legs">
            <template v-for="(group, gIdx) in groupedDetailOdds">

              <!-- ── BetBuilder group ── -->
              <div v-if="group.is_bet_builder" :key="'bb-'+gIdx" class="td-leg td-leg--bb">
                <div class="td-leg-summary">
                  <div class="td-leg-event">
                    <BetBuilderBadge default-text="BB" class="bb-badge-leg" />
                    <span class="td-leg-event-name">{{ group.odds[0].event_description || (group.odds[0].tournament && group.odds[0].tournament.name) }}</span>
                  </div>
                  <div class="td-leg-result-icon">
                    <svg v-if="bbGroupResult(group) === undefined" class="result-svg result-svg--pending" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="10" cy="10" r="8"/><polyline points="10 6 10 10 13 12"/></svg>
                    <svg v-else-if="bbGroupResult(group) === 'win'" class="result-svg result-svg--win" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/></svg>
                    <svg v-else-if="bbGroupResult(group) === 'void'" class="result-svg result-svg--void" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/></svg>
                    <svg v-else class="result-svg result-svg--lose" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/></svg>
                  </div>
                </div>

                <div v-if="detailLegsExpanded" class="td-leg-detail">
                  <div class="td-leg-meta">
                    <span class="td-leg-cat">{{ group.odds[0].category && group.odds[0].category.name }} › {{ group.odds[0].tournament && group.odds[0].tournament.name }}</span>
                    <span class="td-leg-time">
                      {{ new Date(group.odds[0].time).toLocaleDateString('en-GB', { day: 'numeric', month: 'numeric' }) }}
                      {{ new Date(group.odds[0].time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) }}
                    </span>
                  </div>

                  <!-- Timeline BB -->
                  <div class="td-bb-timeline">
                    <div
                      v-for="(odd, idx) in group.odds"
                      :key="odd.unique_id"
                      class="td-bb-row"
                      :class="{ 'td-bb-row--last': idx === group.odds.length - 1 }"
                    >
                      <div class="td-bb-dot"></div>
                      <div class="td-bb-content">
                        <span class="td-bb-outcome">{{ getOutcomeName(odd) }} {{odd.line || odd.specifiers}}</span>
                        <span class="td-bb-market">{{ odd.market_alias || odd.market_name }}</span>
                      </div>
                      <div class="td-bb-result-icon">
                        <svg v-if="odd.result === undefined || odd.result === null" class="result-svg result-svg--pending" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="10" cy="10" r="8"/><polyline points="10 6 10 10 13 12"/></svg>
                        <svg v-else-if="odd.void" class="result-svg result-svg--void" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/></svg>
                        <svg v-else-if="odd.cancelled" class="result-svg result-svg--cancelled" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/></svg>
                        <svg v-else-if="odd.result === true || odd.result === 'win'" class="result-svg result-svg--win" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/></svg>
                        <svg v-else class="result-svg result-svg--lose" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/></svg>
                      </div>
                    </div>
                    <div class="td-leg-live-score">
                      <span class="result" v-if="group.odds[0]?.result_actual_home && group.odds[0]?.result_actual_away">{{ group.odds[0].result_actual_home }} - {{ group.odds[0].result_actual_away }}</span>
                      <span v-if="group.odds[0].time_actual >= 0 && betToShow.status !== 'finished' && group.odds[0].show_time && !group.odds[0].match_result">
                      <v-icon x-small>mdi-timer</v-icon>
                      {{ parseFloat(group.odds[0].time_actual).toFixed(0) }}<span class="blink_me">'</span>
                    </span>
                    </div>

                    <div class="td-bb-combo-row">
                      <span class="td-leg-odd td-bb-combined-odd">{{ getBBCombinedOdd(group) | odds_type_filter }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- ── Leg normale: partita in alto, giocata sotto la linea ── -->
              <div v-else :key="'leg-'+gIdx" class="td-leg" :class="`td-leg--${legResult(group.odds[0])}`">
                <div class="td-leg-match">
                  <div class="td-leg-match-body">
                    <span class="td-leg-teams">
                      <template v-if="legTeams(group.odds[0]).length === 2">
                        {{ legTeams(group.odds[0])[0] }}<span class="td-leg-vs">vs</span>{{ legTeams(group.odds[0])[1] }}
                      </template>
                      <template v-else>{{ group.odds[0].event_description || (group.odds[0].tournament && group.odds[0].tournament.name) }}</template>
                    </span>
                    <span class="td-leg-meta">
                      <span v-if="isLegLive(group.odds[0])" class="badge-odd-live">
                        <span class="badge-odd-live__dot"></span>LIVE
                      </span>
                      <span class="td-leg-cat">{{ [group.odds[0].category && group.odds[0].category.name, group.odds[0].tournament && group.odds[0].tournament.name].filter(Boolean).join(' › ') }}</span>
                      <span class="td-leg-time" v-if="group.odds[0].time">· {{ formatLegTime(group.odds[0].time) }}</span>
                    </span>
                  </div>
                  <div class="td-leg-result-icon">
                    <svg v-if="group.odds[0].result === undefined" class="result-svg result-svg--pending" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="10" cy="10" r="8"/><polyline points="10 6 10 10 13 12"/></svg>
                    <svg v-else-if="group.odds[0].void" class="result-svg result-svg--void" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/></svg>
                    <svg v-else-if="group.odds[0].cancelled" class="result-svg result-svg--cancelled" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/></svg>
                    <svg v-else-if="group.odds[0].result" class="result-svg result-svg--win" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/></svg>
                    <svg v-else class="result-svg result-svg--lose" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/></svg>
                    <v-icon v-if="group.odds[0].half_won" small color="green" style="margin-left:2px">mdi-star-half</v-icon>
                    <v-icon v-else-if="group.odds[0].half_lost" small color="red" style="margin-left:2px">mdi-star-half</v-icon>
                    <span v-if="isLegFrozen(group.odds[0])" class="badge-bet-freeze badge-bet-freeze--leg" :title="$t('betFreeze.frozenLeg')">&#10052;</span>
                    <span v-else-if="isLegFreezeOverridden(group.odds[0])" class="badge-bet-freeze badge-bet-freeze--leg badge-bet-freeze--overridden" :title="legFreezeOverriddenLabel(group.odds[0])">&#10052;</span>
                  </div>
                </div>

                <div v-if="detailLegsExpanded" class="td-leg-pick">
                  <div class="td-leg-pick-main">
                    <span class="td-leg-outcome">{{ getOutcomeName(group.odds[0]) }}</span>
                    <span class="td-leg-market-name">{{ group.odds[0].market_alias || group.odds[0].market_name }} {{ getSpecifiers(group.odds[0].market_unique_id) }}</span>
                    <span v-if="isLegFreezeOverridden(group.odds[0])" class="td-leg-freeze-overridden">&#10052; {{ legFreezeOverriddenLabel(group.odds[0]) }}</span>
                  </div>
                  <span class="td-leg-odd" :class="{ 'td-leg-odd--frozen': isLegFrozen(group.odds[0]) && !group.odds[0].match_result }">
                    <span v-if="group.odds[0].match_result">[{{ group.odds[0].match_result }}]</span>
                    <template v-else-if="isLegFrozen(group.odds[0])">
                      <s class="td-leg-odd-placed">{{ group.odds[0].outcome_odd_value | odds_type_filter }}</s>
                      &#10052; {{ formatFreezeOdd(group.odds[0].betFreeze.frozenOdd) }}
                    </template>
                    <span v-else>{{ group.odds[0].outcome_odd_value | odds_type_filter }}</span>
                  </span>
                </div>

                <div v-if="detailLegsExpanded && (hasLegScore(group.odds[0]) || isLegLive(group.odds[0]))" class="td-leg-live-score">
                  <span class="result" v-if="hasLegScore(group.odds[0])">{{ group.odds[0].result_actual_home }} - {{ group.odds[0].result_actual_away }}</span>
                  <span v-if="isLegLive(group.odds[0])" class="td-leg-clock">
                    <v-icon x-small>mdi-timer-outline</v-icon>
                    {{ parseFloat(group.odds[0].time_actual).toFixed(0) }}<span class="blink_me">'</span>
                  </span>
                </div>

                <!-- betFreeze: action on an open leg (the server decides what is freezable) -->
                <div v-if="detailLegsExpanded && betFreezeLegState(group.odds[0])" class="td-freeze-area" @click.stop>
                  <template v-if="betFreezeConfirm.legUniqueId !== String(group.odds[0].unique_id)">
                    <template v-if="betFreezeLegState(group.odds[0]).freezable">
                      <button class="cashout-btn cashout-btn--hero freeze-hero td-freeze-btn" @click="openBetFreezeConfirm(group.odds[0])">
                        <span class="cashout-btn__label">
                          <v-icon small class="cashout-btn__icon">mdi-snowflake</v-icon>
                          {{ $t('betFreeze.cta') }}
                        </span>
                        <span class="cashout-btn__amount">
                          <s class="freeze-hero__placed">{{ betFreezeLegState(group.odds[0]).initialOdd | odds_type_filter }}</s>
                          <v-icon x-small class="freeze-hero__arrow">mdi-arrow-right</v-icon>
                          {{ formatFreezeOdd(betFreezeLegState(group.odds[0]).frozenOdd) }}
                        </span>
                      </button>
                      <div v-if="betFreezeCost(betToShow, betFreezeLegState(group.odds[0]))" class="td-freeze-cost">
                        {{ $t('betFreeze.cost') }}
                        <span class="td-freeze-cost-value">&minus;{{ betFreezeCost(betToShow, betFreezeLegState(group.odds[0])).amount }} {{ betToShow.currency }} (&minus;{{ betFreezeCost(betToShow, betFreezeLegState(group.odds[0])).percent }}%)</span>
                      </div>
                    </template>
                    <button v-else class="cashout-btn cashout-btn--suspended td-freeze-btn" disabled
                            :title="betFreezeReasonText(betFreezeLegState(group.odds[0]).reason)">
                      <v-icon small>mdi-snowflake</v-icon>
                      {{ $t('betFreeze.action') }} <span class="cashout-btn-reason">· {{ betFreezeReasonText(betFreezeLegState(group.odds[0]).reason) }}</span>
                    </button>
                  </template>
                  <div v-else class="td-freeze-panel">
                    <div class="td-freeze-panel-title"><v-icon small>mdi-snowflake</v-icon> {{ $t('betFreeze.confirmTitle') }}</div>
                    <div class="td-freeze-panel-row">
                      <span>{{ $t('betFreeze.placedOdd') }}</span>
                      <span>{{ betFreezeLegState(group.odds[0]).initialOdd | odds_type_filter }}</span>
                    </div>
                    <div class="td-freeze-panel-row">
                      <span>{{ $t('betFreeze.currentOdd') }}</span>
                      <span>{{ betFreezeLegState(group.odds[0]).currentOdd | odds_type_filter }}</span>
                    </div>
                    <div class="td-freeze-panel-row td-freeze-panel-row--accent">
                      <span>{{ $t('betFreeze.frozenOdd') }}</span>
                      <span>{{ formatFreezeOdd(betFreezeLegState(group.odds[0]).frozenOdd) }}</span>
                    </div>
                    <template v-if="betFreezeFairInfo(betFreezeLegState(group.odds[0]))">
                      <div class="td-freeze-panel-row" :title="$t('betFreeze.fairValueHelp')">
                        <span>{{ $t('betFreeze.fairValue') }} <span class="td-freeze-help">&#9432;</span></span>
                        <span>{{ formatFreezeOdd(betFreezeFairInfo(betFreezeLegState(group.odds[0])).fair) }}</span>
                      </div>
                      <div class="td-freeze-panel-fair">
                        {{ $t('betFreeze.fairValueOffer', betFreezeFairInfo(betFreezeLegState(group.odds[0]))) }}
                      </div>
                    </template>
                    <div v-if="betFreezeLegState(group.odds[0]).estimatedPotentialWinning != null" class="td-freeze-panel-row">
                      <span>{{ $t('betFreeze.newPotentialWinning') }}</span>
                      <span>
                        <s v-if="betFreezeCurrentWinning(betToShow) !== null" class="bet-freeze-original-winning">{{ betFreezeCurrentWinning(betToShow) }}</s>
                        &asymp; {{ parseFloat(betFreezeLegState(group.odds[0]).estimatedPotentialWinning).toFixed(2) }} {{ betToShow.currency }}
                      </span>
                    </div>
                    <template v-if="betFreezeCost(betToShow, betFreezeLegState(group.odds[0]))">
                      <div class="td-freeze-panel-row td-freeze-panel-row--cost">
                        <span>{{ $t('betFreeze.cost') }}</span>
                        <span>&minus;{{ betFreezeCost(betToShow, betFreezeLegState(group.odds[0])).amount }} {{ betToShow.currency }} (&minus;{{ betFreezeCost(betToShow, betFreezeLegState(group.odds[0])).percent }}%)</span>
                      </div>
                      <div class="td-freeze-panel-benefit">{{ $t('betFreeze.benefit') }}</div>
                    </template>
                    <div class="td-freeze-panel-validity">
                      <template v-if="betFreezeConfirm.busy && betFreezeLegState(group.odds[0]) && betFreezeLegState(group.odds[0]).isLive">
                        <div class="spin-sm mr-2"></div>{{ $t('betFreeze.verifying') }}
                      </template>
                      <template v-else-if="betFreezeConfirm.refreshing">
                        <div class="spin-sm mr-2"></div>{{ $t('betFreeze.refreshingPrice') }}
                      </template>
                      <template v-else-if="betFreezeSecondsLeft(group.odds[0]) !== null">
                        {{ $t('betFreeze.validFor', { seconds: betFreezeSecondsLeft(group.odds[0]) }) }}
                      </template>
                    </div>
                    <div class="td-freeze-panel-note">{{ $t('betFreeze.irreversible') }}</div>
                    <div v-if="betFreezeConfirm.message" class="td-freeze-panel-message" :class="`td-freeze-panel-message--${betFreezeConfirm.messageType}`">
                      {{ betFreezeConfirm.message }}
                    </div>
                    <div class="cashout-inline-actions td-freeze-panel-actions">
                      <button class="dialog-close-btn" :disabled="betFreezeConfirm.busy" @click="closeBetFreezeConfirm">{{ $t('betFreeze.cancel') }}</button>
                      <button class="cashout-confirm-btn freeze-confirm-btn" :class="{ 'is-loading': betFreezeConfirm.busy }"
                              :disabled="betFreezeConfirm.busy || betFreezeConfirm.refreshing" @click="confirmBetFreeze(group.odds[0])">
                        <span v-if="!betFreezeConfirm.busy">{{ $t('betFreeze.confirm') }}</span>
                        <span v-else class="btn-spinner-inline"></span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

            </template>
          </div>
        </section>

        </div><!-- end td-scroll-body -->
        </div>
        </transition>
      </div>
    </transition>

    <transition name="td-backdrop-fade">
      <div v-if="dialog_betlist && !getModalIdle" class="td-overlay-backdrop all-bets-backdrop" @click.self="dialog_betlist = false">
        <transition name="td-sheet">
          <div v-if="dialog_betlist" class="all-bets-card">

            <div class="td-topbar ab-topbar">
              <div class="td-code-block">
                <span class="ab-title">{{ $t('cartSheet.allBets') }}</span>
                <span class="ab-count">{{ getMyBetsAll.length }}</span>
              </div>
              <div class="td-topbar-right">
                <button class="td-icon-btn" :aria-label="$t('close')" @click="dialog_betlist = false">
                  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
                </button>
              </div>
            </div>

            <div class="all-bets-body">
              <div v-if="overlay_dialog_betlist" class="td-overlay td-overlay-absolute">
                <div class="spinner-lg"></div>
              </div>

              <!-- ── Riepilogo: netto in evidenza, poi i totali ── -->
              <section class="ab-summary">
                <div class="ab-net">
                  <span class="ab-net-label">{{ $t('net') }}</span>
                  <span class="ab-net-value" :class="mybets_net >= 0 ? 'is-positive' : 'is-negative'">
                    {{ mybets_net > 0 ? '+' : '' }}{{ formatPrice(mybets_net) }} <small>{{ mybets_currency }}</small>
                  </span>
                </div>
                <div class="ab-stats">
                  <div class="ab-stat">
                    <span class="ab-stat-label">{{ $t('myBets') }}</span>
                    <span class="ab-stat-value">{{ mybets_total }}</span>
                  </div>
                  <div class="ab-stat">
                    <span class="ab-stat-label">{{ $t('totalPlayed') }}</span>
                    <span class="ab-stat-value">{{ formatPrice(mybets_total_played) }}</span>
                  </div>
                  <div class="ab-stat">
                    <span class="ab-stat-label">{{ $t('totalWon') }}</span>
                    <span class="ab-stat-value is-win">{{ formatPrice(mybets_winner) }}</span>
                  </div>
                  <div class="ab-stat">
                    <span class="ab-stat-label">{{ $t('potentialWinnings') }}</span>
                    <span class="ab-stat-value is-pending">{{ formatPrice(mybets_pending) }}</span>
                  </div>
                  <div class="ab-stat">
                    <span class="ab-stat-label">{{ $t('void') }}</span>
                    <span class="ab-stat-value">{{ formatPrice(mybets_void) }}</span>
                  </div>
                </div>
              </section>

              <!-- ── Filtri ── -->
              <section class="ab-filters">
                <div class="ab-chips" role="tablist">
                  <button
                      v-for="item in quickResults"
                      :key="'r-' + item.value"
                      class="ab-chip"
                      role="tab"
                      :aria-selected="filterResult === item.value"
                      :class="{ active: filterResult === item.value }"
                      @click="setResultFilter(item.value)"
                  >{{ item.text }}</button>
                </div>

                <div class="ab-period">
                  <div class="ab-chips ab-chips--period">
                    <button
                        v-for="days in [7, 30, 90]"
                        :key="'p-' + days"
                        class="ab-chip ab-chip--sm"
                        :class="{ active: activePeriod === days }"
                        @click="setPeriod(days)"
                    >{{ $t('cartSheet.lastDays', { n: days }) }}</button>
                  </div>
                  <div class="ab-dates">
                    <label class="ab-date">
                      <span>{{ $t('generic.from') }}</span>
                      <input type="date" class="custom-input custom-date" v-model="filterFromTime" @change="searchBets">
                    </label>
                    <label class="ab-date">
                      <span>{{ $t('generic.to') }}</span>
                      <input type="date" class="custom-input custom-date" v-model="filterToTime" @change="searchBets">
                    </label>
                  </div>
                </div>

                <button class="ab-more-toggle" @click="showAdvancedFilters = !showAdvancedFilters">
                  <v-icon small>mdi-tune-variant</v-icon>
                  <span>{{ $t('cartSheet.moreFilters') }}</span>
                  <span v-if="advancedFiltersCount" class="ab-more-count">{{ advancedFiltersCount }}</span>
                  <svg class="ab-more-chevron" :class="{ open: showAdvancedFilters }" viewBox="0 0 24 24" fill="currentColor" width="16" height="16"><path d="M7 10l5 5 5-5z"/></svg>
                </button>

                <div v-if="showAdvancedFilters" class="ab-advanced">
                  <div class="filter-grid">
                    <div class="filter-col" v-if="!getShortCode">
                      <label class="filter-label">{{ $t('bets.code') }}</label>
                      <input type="text" class="custom-input" v-model="filterCode" :placeholder="$t('bets.code')" @keyup.enter="searchBets">
                    </div>
                    <div class="filter-col" v-if="getShortCode">
                      <label class="filter-label">{{ $t('bets.code') }}</label>
                      <input type="text" class="custom-input" v-model="filterBetCode" :placeholder="$t('bets.code')" @keyup.enter="searchBets">
                    </div>
                    <div class="filter-col">
                      <label class="filter-label">{{ $t('generic.context') }}</label>
                      <select class="custom-select text-capitalize" v-model="filterContext">
                        <option v-for="item in typeOfContexts" :key="item.value" :value="item.value">{{ item.text }}</option>
                      </select>
                    </div>
                    <div class="filter-col">
                      <label class="filter-label">{{ $t('bets.type') }}</label>
                      <select class="custom-select text-capitalize" v-model="filterType">
                        <option v-for="item in typeOfBets" :key="item.value" :value="item.value">{{ item.text }}</option>
                      </select>
                    </div>
                    <div class="filter-col">
                      <label class="filter-label">{{ $t('bets.payed') }}</label>
                      <select class="custom-select text-capitalize" v-model="filterPayed">
                        <option v-for="item in typeOfPayed" :key="item.value" :value="item.value">{{ item.text }}</option>
                      </select>
                    </div>
                  </div>

                  <div v-if="(getUserType && getUserType == 4) || (getCashier != null && getCashier === true)" class="custom-radio-group">
                    <label class="custom-radio">
                      <input type="radio" :value="false" v-model="showOnlyMyBets">
                      <span class="radio-mark"></span>
                      <span class="radio-text">{{ $t('includeCashierBets') }}</span>
                    </label>
                    <label class="custom-radio">
                      <input type="radio" :value="true" v-model="showOnlyMyBets">
                      <span class="radio-mark"></span>
                      <span class="radio-text">{{ $t('viewOnlyPerformedByMe') }}</span>
                    </label>
                  </div>

                  <div class="filter-btn-group">
                    <button class="filter-btn filter-btn--reset" @click="resetFilter">{{ $t('generic.reset') }}</button>
                    <button class="filter-btn filter-btn--search" @click="searchBets">{{ $t('generic.search') }}</button>
                  </div>
                </div>
              </section>

              <!-- ── Lista: stesse righe di My Bets ── -->
              <section class="ab-list">
                <div v-if="getMyBetsAll.length === 0" class="ab-empty">
                  <div class="empty-state__icon-wrap">
                    <svg viewBox="0 0 24 24" fill="none" width="28" height="28"><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><rect x="9" y="3" width="6" height="4" rx="1" stroke="currentColor" stroke-width="1.5"/><path d="M9 12h6M9 16h4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
                  </div>
                  <span class="empty-state__title">{{ $t('noMyBets') }}</span>
                </div>

                <div
                    v-for="item in getMyBetsAll"
                    :key="item.code"
                    :class="['single-bet', `single-bet--${item.result}`]"
                    @click="handleClick(item)"
                >
                  <div class="bet-info">
                    <div class="bet-row bet-row--head">
                      <div class="bet-type">
                        <span class="bet-type-name">{{ $t(item.type) }}</span>
                        <span v-if="item.odds && item.odds.length" class="bet-event-count">({{ item.odds.length }})</span>
                        <span class="bet-date">· {{ formatBetDate(item.created_at) }}</span>
                        <span v-if="item.bet_context === 'live'" class="icon-live" :title="$t('live')" :aria-label="$t('live')"><v-icon x-small>mdi-access-point</v-icon></span>
                      </div>
                      <span class="bet-status-badge" :class="`bet-status-badge--${item.result}`">{{ $t('bets.'+item.result) }}</span>
                    </div>

                    <div class="bet-row bet-row--meta">
                      <span class="bet-money">
                        <span class="bet-money-label">{{ $t('amount') }}:</span>
                        <span class="bet-money-amount">{{ parseFloat(item.amount_real).toFixed(2) }} {{ item.currency }}</span>
                      </span>
                      <span class="bet-money">
                        <span class="bet-money-label">{{ $t('winning') }}:</span>
                        <span class="bet-money-win" :class="`bet-money-win--${item.result}`">
                          {{ item.winning_real ? parseFloat(item.winning_real).toFixed(2) + ' ' + item.currency : getWinning(item) }}
                        </span>
                      </span>
                    </div>

                    <div class="bet-row ab-row-foot">
                      <span class="ab-code">{{ getShortCode && item.betCode ? item.betCode : item.code }}</span>
                      <span class="ab-paid" :class="{ 'is-paid': item.payed !== 'false' }">
                        {{ $t('bets.payed') }}: {{ item.payed === 'false' ? $t('generic.no') : $t('generic.yes') }}
                      </span>
                    </div>
                  </div>
                </div>
              </section>

            </div>
          </div>
        </transition>
      </div>
    </transition>
    </div>

  <BetShareReceipt
    ref="shareBetReceipt"
    :bet="betToShow"
    :currency-symbol="betToShow.currency || ''"
    @share-end="loadingShareImage = false"
  />

  <PrintReceiptBet
    ref="printReceipt"
    :bet="betToShow"
    :currency-symbol="betToShow.currency || ''"
  />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { isMobileOnly } from 'mobile-device-detect';
import orderByLodash from 'lodash/orderBy';
import { checkIsMobileDevice, checkIsTabletDevice } from '../../plugins/device-inspector';
import { CASHOUT_TEMPORARY_POLL_INTERVAL_MS, CASHOUT_VISIBLE_STATUSES, CASHOUT_MAX_CONCURRENT, CASHOUT_WORKER_STAGGER_MS, CASHOUT_TASK_GAP_MS } from '@/store/modules/cashout';
import { runWorkerPool, createSleeper } from '@/utils/cashoutWorkerPool';
import betFreezeDetailMixin from '@/mixins/betFreezeDetailMixin';
import betFreezeListMixin from '@/mixins/betFreezeListMixin';
import ScoreboardLive from "@/components/center_widgets/Live/previews/ScoreboardLive.vue";
import BetShareReceipt from './BetShareReceipt.vue';
import PrintReceiptBet from '../generic/PrintReceiptBet.vue';
import BetBuilderBadge from '@/components/badges/BetBuilderBadge.vue';

export default {
  name: 'MyBets',
  components: { ScoreboardLive, BetShareReceipt, PrintReceiptBet, BetBuilderBadge },
  inject: { cartSheet: { default: false } },
  // betFreeze logic, shared with the American console (IllinoisMyBets / BetDetailModal).
  mixins: [betFreezeDetailMixin, betFreezeListMixin],
  props: {
    /**
     * Lists only bets that could still be cashed out.
     *
     * Set by the American cart, which renders this component under a tab
     * literally called Cashout: a settled bet can never be cashed out, so
     * showing won and lost ones there is noise. The classic cart lists every
     * bet under My Bets and leaves this off.
     */
    cashoutOnly: { type: Boolean, default: false },
  },
  data() {
    return {
      dialog: false,
      dialog_betlist: false,
      overlay_dialog_betlist: false,
      overlay_dialog_betlist_detail: false,
      betToShow: {},
      betToShowAll: {},
      loading: false,
      overlay: false,
      visibleBetsCount: 5,
      cashoutConfirmBet: null,
      cashoutConfirmData: null,
      cashoutExecuting: false,
      cashoutFrozenExpired: false,
      cashoutInlineStep: 'confirm',
      cashoutModalInlineOpen: false,
      cashoutBypassLoading: false,
      cashoutFreshData: null,
      showFullCode: false,
      loadingShareImage: false,
      detailLegsExpanded: true,
      isMobileOnly: isMobileOnly,
      mybets_total: 0,
      mybets_total_played: 0,
      mybets_winner: 0,
      mybets_void: 0,
      mybets_pending: 0,
      mybets_net: 0,
      mybets_currency: '',
      enableTimezone: true,
      filterType: '',
      filterResult: '',
      filterPayed: '',
      filterCode: '',
      filterBetCode: '',
      filterContext: '',
      filterFromTime: new Date(new Date(new Date()).setMonth(new Date(new Date()).getMonth() - 1)).toISOString().substr(0, 10),
      filterToTime: new Date(new Date()).toISOString().substr(0, 10),
      filterPlatform: '',
      typeOfBets: [
        { text: _.capitalize(this.$t('generic.all')), value: '' },
        { text: _.capitalize(this.$t('bets.single')), value: 'single' },
        { text: _.capitalize(this.$t('bets.multiple')), value: 'multiple' },
        { text: _.capitalize(this.$t('bets.integral')), value: 'integral' },
        { text: _.capitalize(this.$t('bets.system')), value: 'system' },
      ],
      typeOfResults: [
        { text: _.capitalize(this.$t('generic.all')), value: '' },
        { text: _.capitalize(this.$t('bets.pending')), value: 'pending' },
        { text: _.capitalize(this.$t('bets.win')), value: 'win' },
        { text: _.capitalize(this.$t('bets.lose')), value: 'lose' },
        { text: _.capitalize(this.$t('bets.void')), value: 'void' },
        { text: _.capitalize(this.$t('bets.cancelled')), value: 'cancelled' },
      ],
      typeOfContexts: [
        { text: _.capitalize(this.$t('generic.all')), value: '' },
        { text: _.capitalize(this.$t('bets.prematch')), value: 'prematch' },
        { text: _.capitalize(this.$t('bets.live')), value: 'live' },
        { text: _.capitalize(this.$t('bets.mixed')), value: 'mixed' },
        { text: _.capitalize(this.$t('bets.antepost')), value: 'antepost' },
      ],
      typeOfStatus: [
        { text: _.capitalize(this.$t('generic.all')), value: '' },
        { text: _.capitalize(this.$t('bets.accepted')), value: 'accepted' },
        { text: _.capitalize(this.$t('bets.review')), value: 'review' },
        { text: _.capitalize(this.$t('bets.finished')), value: 'finished' },
      ],
      typeOfPayed: [
        { text: _.capitalize(this.$t('generic.all')), value: '' },
        { text: _.capitalize(this.$t('generic.yes')), value: 'yes' },
        { text: _.capitalize(this.$t('generic.no')), value: 'no' },
      ],
      showOnlyMyBets: true,
      showAdvancedFilters: false,
      detailFromAllBets: false,
    };
  },
  computed: {
    ...mapGetters({
      'getLanguage': 'settings/getLanguage',
      'getRulesManager': 'rules/getRulesManager',
      'getRulesSkin': 'rules/getRulesSkin',
      'getAuthenticated': 'user/getAuthenticated',
      'getMyBets': 'user/getMyBets',
      'getMyBetsAll': 'user/getMyBetsAll',
      'getPlatformSettings': 'settings/getPlatformSettings',
      'getDetailMyBets': 'user/getDetailMyBets',
      'getContextCart': 'user/getContextCart',
      'getModalIdle': 'user/getModalIdle',
      'getOddsType': 'odds/getOddsType',
      'getB2bEnabled': 'settings/getB2bEnabled',
      'getShortCode': 'settings/getShortCode',
      'getType': 'user/getType',
      'getId': 'user/getId',
      'getUserType': 'user/getType',
      'getCashier': 'user/getCashier',
      'getProfileType': 'user/getProfileType',
      'getCashoutByCode': 'cashout/getCashoutByCode',
      'getCashoutPollIntervalMs': 'settings/getCashoutPollIntervalMs',
      'getEnableAgencyCashout': 'settings/getEnableAgencyCashout',
      'getEnableCashierCashout': 'settings/getEnableCashierCashout',
      'getEnableTerminalCashout': 'settings/getEnableTerminalCashout',
      'getTerminal': 'user/getTerminal',
    }),
    // Esiti come chip rapidi: gli annullati non arrivano mai dalla lista
    // (fetchMyBetsAllWithoutCancelled), quindi il chip sarebbe sempre vuoto.
    quickResults() {
      return this.typeOfResults.filter(item => item.value !== 'cancelled');
    },
    activePeriod() {
      const today = new Date().toISOString().substr(0, 10);
      if (this.filterToTime !== today || !this.filterFromTime) return null;
      const days = Math.round((new Date(this.filterToTime) - new Date(this.filterFromTime)) / 864e5);
      return [7, 30, 90].find(d => Math.abs(days - d) <= 1) || null;
    },
    advancedFiltersCount() {
      return [this.filterCode, this.filterBetCode, this.filterContext, this.filterType, this.filterPayed]
        .filter(v => v !== '' && v != null).length;
    },
    detailEventsCount() {
      const odds = (this.getDetailMyBets && this.getDetailMyBets.odds) || (this.betToShow && this.betToShow.odds) || [];
      return odds.length;
    },
    sheetMode() {
      return this.cartSheet && !this.cashoutOnly;
    },
    cashoutAmountForConfirm() {
      if (!this.cashoutConfirmBet || !this.cashoutConfirmData) return null;
      const { amount } = this.cashoutConfirmData;
      return amount ?? null;
    },
    isPendingAndCashoutEligible() {
      return this.betToShow && this.betToShow.result === 'pending' && this.isCashoutEligible(this.betToShow);
    },
    groupedDetailOdds() {
      const odds = (this.getDetailMyBets && this.getDetailMyBets.odds)
        || (this.betToShow && this.betToShow.odds)
        || [];
      const map = new Map();
      for (const odd of odds) {
        const key = odd.bet_builder_id || odd.unique_id;
        if (!map.has(key)) {
          map.set(key, {
            key,
            is_bet_builder: !!odd.bet_builder_id,
            odds: [],
          });
        }
        map.get(key).odds.push(odd);
      }
      return Array.from(map.values());
    },
    isPlayerEligibleForCashout() {
      const profileType = this.getProfileType;
      const isAgency    = this.getUserType != null && this.getUserType == 4;
      const isCashier   = profileType === 'CASHIER' || !!this.getCashier;
      const isTerminal  = profileType === 'TERMINAL' || !!this.getTerminal;
      // Each operator profile needs its own skin flag; a plain player is
      // always eligible. The getters this reads (getProfileType, getUserType,
      // getCashier, getTerminal and the three enable* flags) are all visible in
      // Vue devtools, which is where a "why is cashout missing" question gets
      // answered now that the trace that used to print them is gone.
      if (isAgency   && this.getEnableAgencyCashout)   return true;
      if (isCashier  && this.getEnableCashierCashout)  return true;
      if (isTerminal && this.getEnableTerminalCashout) return true;

      return !isAgency && !isCashier && !isTerminal;
    },
  },
  watch: {
    async getDetailMyBets(val) {
      if (this.dialog) {
        this.betToShow = val;
      }
    },
    overlay(val) {
      val && setTimeout(() => { this.overlay = false; }, 3000);
    },
    showOnlyMyBets(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.initialize();
      }
    },
    dialog(val) {
      if (!val) this._stopBetFreezeClock();
      document.body.style.overflow = val ? 'hidden' : '';
      this.$root.$emit('bet-detail-open', val);
    },
    dialog_betlist(val) {
      document.body.style.overflow = val ? 'hidden' : '';
    }
  },
  methods: {
    ...mapActions({
      'fetchMyBets': 'user/fetchMyBets',
      'fetchMyBetsAll': 'user/fetchMyBetsAll',
      'fetchMyBetsAllFilter': 'user/fetchMyBetsAllFilter',
      'fetchMyBetsAllWithoutCancelled': 'user/fetchMyBetsAllWithoutCancelled',
      'resetCart': 'cart/resetCart',
      'setOddsInCart': 'cart/setOddsInCart',
      'refreshCart': 'cart/refreshCart',
      'setCreatedAtCart': 'cart/setCreatedAtCart',
      'fetchLiveScores': 'odds/fetchLiveScores',
      'setDetailMyBet': 'user/setDetailMyBet',
      'setReceiptPrinted': 'sport/setReceiptPrinted',
      'calculateCashout': 'cashout/calculateCashout',
      'executeCashout': 'cashout/executeCashout',
      'setSnackbarText': 'settings/setSnackbarText',
      'setSnackbar': 'settings/setSnackbar',
    }),
    getOutcomeName(odd) {
      const name = odd.outcome_name;
      if (!name) return odd.outcome_alias || '';
      if (name === 'X') return odd.outcome_alias || odd.outcome_name;
      const lower = name.toLowerCase();
      const c = odd.competitors;
      if (lower === 'local' && c && c[0]) return c[0].name;
      if (lower === 'visitante' && c && c[1]) return c[1].name;
      if ((name === '1' || name === '2') && odd.unique_id !== '128' && c) {
        const found = c.find(function(t) { return t.position === name; });
        if (found) return found.name;
      }
      return odd.outcome_alias || name;
    },
    getBBCombinedOdd(group) {
      const main = group.odds.find(o => parseFloat(o.outcome_odd_value) !== 1);
      return main ? main.outcome_odd_value : (group.odds[0] ? group.odds[0].outcome_odd_value : 0);
    },
    bbGroupResult(group) {
      // Restituisce undefined se nessuna odd ha result (pending), 'win' se tutte vinte, altrimenti 'lose'
      const results = group.odds.map(o => o.result);
      if (results.every(r => r === undefined || r === null)) return undefined;
      if (group.odds.some(o => o.void || o.cancelled)) return 'void';
      if (results.every(r => r === true || r === 'win')) return 'win';
      return 'lose';
    },

    // Stesso canale usato da "Scommetti" senza login (ActionsMarketView).
    requestLogin() {
      // Chiude il carrello prima del login: la skin ricarica l'iframe a login
      // avvenuto e deve ritrovare il carrello chiuso, non uno stato appeso.
      this.$root.$emit('close_sidebar_right', true);
      if (typeof window !== 'undefined' && window.parent !== window) {
        window.parent.postMessage({ type: 'OPEN_LOGIN' }, '*');
      }
    },
    formatBetDate(value) {
      const date = new Date(value);
      if (isNaN(date.getTime())) return '';
      return date.toLocaleDateString(undefined, { day: '2-digit', month: '2-digit' }) + ' · ' +
        date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    },
    legTeams(odd) {
      const c = odd && odd.competitors;
      if (Array.isArray(c) && c.length === 2 && c[0] && c[1] && c[0].name && c[1].name) return [c[0].name, c[1].name];
      return [];
    },
    // Scommessa giocata con una freebet: il backend valorizza freeBetId.
    isFreeBetBet(bet) {
      return !!bet && bet.freeBetId != null && bet.freeBetId !== '';
    },
    isLegLive(odd) {
      return this.betToShow.status !== 'finished' && !!odd.show_time && odd.time_actual >= 0 && !odd.match_result;
    },
    hasLegScore(odd) {
      const ok = v => v !== undefined && v !== null && v !== '';
      return ok(odd.result_actual_home) && ok(odd.result_actual_away);
    },
    legResult(odd) {
      if (odd.result === undefined || odd.result === null) return 'pending';
      if (odd.void || odd.cancelled) return 'void';
      return odd.result ? 'win' : 'lose';
    },
    formatLegTime(ms) {
      const date = new Date(Number(ms));
      if (isNaN(date.getTime())) return '';
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' }) + ' ' +
        date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    },

    mountDialogLayer() {
      const layer = this.$refs.dialogLayer;
      const appRoot = this.$root && this.$root.$el;
      if (!layer || !appRoot || !appRoot.appendChild) return;
      const target = appRoot.querySelector('.v-application') || appRoot;
      target.appendChild(layer);
      this._dialogLayer = layer;
    },

    async initialize() {
      this.loading = true;
      const isAgencyOrCashier = (this.getUserType != null && this.getUserType == 4) || (this.getCashier != null && this.getCashier === true);
      if (isAgencyOrCashier) {
        await this.fetchMyBets({ all: false, showOnlyMyBets: this.showOnlyMyBets });
      } else {
        await this.fetchMyBets({ all: false });
      }
      this.loading = false;
      await this._triggerCashoutForPendingBets();
      this._checkBetFreezeForList();
      this._startCashoutPoller();
      this._startTemporaryPoller();
    },

    /**
     * The bets the list actually renders.
     *
     * With `cashoutOnly` it keeps pending bets whose type can be cashed out at
     * all. Both checks are synchronous and local — `result` comes with the bet
     * and `isCashoutEligible` only looks at its type — so the list is stable
     * from the first paint. Filtering on `getCashoutByCode(...).status` instead
     * would be more precise but would make rows disappear as the cashout
     * requests land, one by one, under the user's cursor.
     */
    _visibleBets() {
      if (!this.cashoutOnly) return this.getMyBets;
      return this.getMyBets.filter(b => b && b.result === 'pending' && this.isCashoutEligible(b));
    },

    /**
     * Short reason shown inside a disabled cashout button, from the error code
     * the server returned (store `errorCode`). `withEvent` appends the
     * blocking event (tooltip) for suspended odds when known.
     */
    cashoutReasonText(entry, withEvent = false) {
      if (!entry) return '';
      if (entry.status === 'temporary') return this.$t('cashout.reasons.TEMPORARY');
      const code = entry.errorCode || '';
      let key = 'DEFAULT';
      if (code === 'CASHOUT_NOT_AVAILABLE_YET') key = 'NOT_AVAILABLE_YET';
      else if (['CASHOUT_SUSPENDED', 'ODDS_SUSPENDED', 'SOME_EVENTS_SUSPENDED'].includes(code)) key = 'SUSPENDED';
      else if (code === 'OUTCOME_LAST_UPDATE_OVERDUE') key = 'STALE_ODDS';
      else if (code === 'PARTIAL_OUTCOME') key = 'PARTIAL_OUTCOME';
      else if (code.startsWith('GLOBAL_') && code.endsWith('_DISABLED')) key = 'DISABLED';
      const text = this.$t(`cashout.reasons.${key}`);
      return withEvent && key === 'SUSPENDED' && entry.suspendedEventName ? `${text} · ${entry.suspendedEventName}` : text;
    },

    isCashoutEligible(bet) {
      // No cashout on a bet with a bet builder (rocky-bets and rocky-definition refuse it too).
      if (this.hasBetBuilderLeg(bet)) return false;
      const INELIGIBLE = ['integral', 'system'];
      // No cashout on a bet with a betFreeze applied (enforced server-side too).
      if (bet.betFreeze === true || (bet.betFreezeCount || 0) > 0) return false;
      return !INELIGIBLE.includes(bet.type);
    },
    // Pending bet builder bet of a player who could otherwise cash out: the server refuses
    // the cashout (BET_BUILDER_CASHOUT_NOT_ALLOWED), so say why instead of a missing button.
    showBetBuilderCashoutNote(bet) {
      return Boolean(bet) && bet.result === 'pending' && this.$store.getters['settings/getCashout'] === true
        && this.isPlayerEligibleForCashout && this.hasBetBuilderLeg(bet);
    },
    hasBetBuilderLeg(bet) {
      return Boolean(bet) && (bet.odds || []).some(odd => odd && odd.is_bet_builder === true);
    },

    getCashoutAmountForBet(bet) {
      const entry = this.getCashoutByCode(bet.code);
      if (!entry || entry.amount == null) return null;
      return entry.amount;
    },

    formatCashoutAmount(bet) {
      const amount = this.getCashoutAmountForBet(bet);
      return amount != null ? amount.toFixed(2) : '';
    },

    isCashoutVisible(bet) {
      return CASHOUT_VISIBLE_STATUSES.has(this.getCashoutByCode(bet.code).status);
    },

    formatCashoutLastUpdated(bet) {
      const entry = this.getCashoutByCode(bet.code);
      if (!entry || !entry.lastUpdated) return '';
      const diffSec = Math.floor((Date.now() - entry.lastUpdated) / 1000);
      if (diffSec < 60) return `${diffSec}s`;
      return `${Math.floor(diffSec / 60)}m`;
    },


    _pendingBets() {
      if (!this.isPlayerEligibleForCashout) return [];
      return this.getMyBets.filter(b => {
        if (b.result !== 'pending') return false;
        if (!this.isCashoutEligible(b)) return false;
        if (this.getCashoutByCode(b.code).status === 'hidden') return false;
        return true;
      });
    },

    /**
     * Runs `calculateCashout` over `codes` with at most CASHOUT_MAX_CONCURRENT
     * requests in flight, and returns only once every one of them has settled.
     *
     * Replaces the previous fan-out, which launched one request per pending bet
     * with a blind 200ms gap between launches and never awaited them: with a
     * slow backend the requests piled up without limit, and the 'temporary'
     * poller did not even stagger — it fired its whole set at once every minute.
     *
     * Shape: a fixed number of workers pulling from a shared cursor. The cap is
     * on requests IN FLIGHT, not on total requests, so every bet still gets its
     * amount — it just cannot flood the server.
     *
     * Pacing (src/utils/cashoutWorkerPool.js): at most CASHOUT_MAX_CONCURRENT
     * in flight, workers started CASHOUT_WORKER_STAGGER_MS apart, a
     * CASHOUT_TASK_GAP_MS pause between two requests of the same worker.
     *
     * Leak safety:
     *  - each task is wrapped in try/catch, so one rejection cannot kill a
     *    worker and strand `Promise.all` unresolved forever;
     *  - workers stop pulling as soon as `_cashoutDestroyed` is set, so a
     *    closed panel does not keep issuing requests;
     *  - pauses go through `_cashoutSleeper`, cancelled on destroy: no timer
     *    outlives the component;
     *  - the pool state is local and dropped when this resolves — nothing
     *    retains the promises or the codes;
     *  - a hung request cannot hold a slot indefinitely: the action passes
     *    CASHOUT_REQUEST_TIMEOUT_MS to axios.
     */
    async _runCashoutPool(codes) {
      await runWorkerPool(codes, code => this.calculateCashout({ code }), {
        concurrency: CASHOUT_MAX_CONCURRENT,
        staggerMs: CASHOUT_WORKER_STAGGER_MS,
        gapMs: CASHOUT_TASK_GAP_MS,
        isCancelled: () => this._cashoutDestroyed === true,
        sleep: ms => (this._cashoutDestroyed || !this._cashoutSleeper ? Promise.resolve() : this._cashoutSleeper.sleep(ms)),
        // calculateCashout swallows its own HTTP errors, so reaching this means
        // the action itself broke: contained per task, the pool keeps going.
        onError: (code, e) => console.warn('[cashout] pool task failed for', code, e && e.message),
      });
    },

    /**
     * One shared re-entrancy guard for BOTH pollers.
     *
     * This is the part that stops requests growing without bound. Both pollers
     * are on setInterval: if a run takes longer than its interval — a slow
     * backend, or simply many bets — the next tick used to start anyway, on top
     * of the one still running, and so on. A single guard means at most ONE
     * pool is ever active, so in-flight requests are capped at
     * CASHOUT_MAX_CONCURRENT globally and never at a multiple of it.
     *
     * Skipping a 'temporary' run while the main one is busy loses nothing:
     * `_temporaryBets()` is a strict subset of `_pendingBets()` (same pending +
     * eligible filter, narrowed to status 'temporary', which is not 'hidden'),
     * so the main poller already refreshes those bets.
     *
     * The `finally` is not optional — without it a throw would leave the guard
     * closed and silently kill every later poll.
     */
    async _runGuardedCashoutPool(codes) {
      if (this._cashoutDestroyed) return;
      if (this._cashoutRunActive) return;
      this._cashoutRunActive = true;
      try {
        await this._runCashoutPool(codes);
      } finally {
        this._cashoutRunActive = false;
      }
    },

    async _triggerCashoutForPendingBets() {
      const codes = this._pendingBets().map(b => b.code).filter(Boolean);
      await this._runGuardedCashoutPool(codes);
    },

    async _triggerCashoutForTemporaryBets() {
      const codes = this._temporaryBets().map(b => b.code).filter(Boolean);
      await this._runGuardedCashoutPool(codes);
    },

    _startCashoutPoller() {
      this._stopCashoutPoller();
      this._cashoutPollTimer = setInterval(
          () => this._triggerCashoutForPendingBets(),
          this.getCashoutPollIntervalMs,
      );
    },

    _stopCashoutPoller() {
      if (this._cashoutPollTimer) {
        clearInterval(this._cashoutPollTimer);
        this._cashoutPollTimer = null;
      }
    },

    _temporaryBets() {
      if (!this.isPlayerEligibleForCashout) return [];
      return this.getMyBets.filter(b =>
          b.result === 'pending' &&
          this.isCashoutEligible(b) &&
          b.code &&
          this.getCashoutByCode(b.code).status === 'temporary',
      );
    },

    _startTemporaryPoller() {
      this._stopTemporaryPoller();
      this._cashoutTemporaryTimer = setInterval(
          () => this._triggerCashoutForTemporaryBets(),
          CASHOUT_TEMPORARY_POLL_INTERVAL_MS,
      );
    },

    _stopTemporaryPoller() {
      if (this._cashoutTemporaryTimer) {
        clearInterval(this._cashoutTemporaryTimer);
        this._cashoutTemporaryTimer = null;
      }
    },

    checkIsMobile() {
      return checkIsMobileDevice();
    },

    checkIsTablet() {
      return checkIsTabletDevice();
    },

    sortby(array) {
      return array.length > 0 ? orderByLodash(array, ['source'], ['asc']) : [];
    },

    result(type) {
      const map = {
        1: this.$t('statistics.corner'),
        6: this.$t('statistics.yellow'),
        7: this.$t('statistics.red'),
        8: this.$t('statistics.penalties'),
        9: this.$t('statistics.goal'),
        10: this.$t('statistics.substitutions'),
        12: this.$t('statistics.fouls'),
        20: this.$t('statistics.aces'),
        21: this.$t('statistics.doublefaults'),
        24: this.$t('statistics.owngoal'),
        25: this.$t('statistics.penaltygoal'),
        28: this.$t('statistics.twopoints'),
        30: this.$t('statistics.threepoints'),
        31: this.$t('statistics.timeouts'),
        32: this.$t('statistics.freethrows'),
        33: this.$t('statistics.hits'),
        34: this.$t('statistics.firstservewins'),
        40: this.$t('statistics.missedpenalty'),
      };
      return map[type] !== undefined ? map[type] : type;
    },

    resultChipColor(result) {
      const map = { win: 'green darken-1', lose: 'red darken-1', pending: 'orange darken-1', void: 'blue darken-1' };
      return map[result] || 'grey';
    },

    openCashoutConfirm(bet, cashoutEntry) {
      if (this.cashoutConfirmBet && this.cashoutConfirmBet.code === bet.code) {
        this.closeCashoutInline();
        return;
      }
      this.cashoutConfirmBet = bet;
      this.cashoutConfirmData = cashoutEntry;
      this.cashoutFrozenExpired = false;
      this.cashoutModalInlineOpen = false;
      this.cashoutInlineStep = 'confirm';
    },

    openModalCashout(bet, cashoutEntry) {
      this.cashoutConfirmBet = bet;
      this.cashoutConfirmData = cashoutEntry;
      this.cashoutFrozenExpired = false;
      this.cashoutInlineStep = 'confirm';
      this.cashoutModalInlineOpen = true;
    },

    closeCashoutInline() {
      this.cashoutConfirmBet = null;
      this.cashoutConfirmData = null;
      this.cashoutFreshData = null;
      this.cashoutInlineStep = 'confirm';
      this.cashoutModalInlineOpen = false;
    },

    async confirmCashout() {
      if (!this.cashoutConfirmBet) return;
      const code = this.cashoutConfirmBet.code;
      this.cashoutFreshData = null;
      this.cashoutBypassLoading = true;
      this.cashoutInlineStep = 'double';
      await this.calculateCashout({ code, bypassCache: true });
      const fresh = this.getCashoutByCode(code);
      this.cashoutBypassLoading = false;
      if (fresh.status === 'available') {
        this.cashoutFreshData = fresh;
      } else {
        this.closeCashoutInline();
      }
    },

    async proceedCashout() {
      if (!this.cashoutConfirmBet) return;
      const code = this.cashoutConfirmBet.code;
      this.cashoutExecuting = true;
      try {
        const result = await this.executeCashout({ code });
        if (result.success) {
          const wasModalOpen = this.dialog;
          this.closeCashoutInline();
          await this.initialize();
          if (wasModalOpen) {
            const updated = this.getMyBets.find(b => b.code === code);
            if (updated) {
              this.betToShow = updated;
              this.setDetailMyBet(updated);
            }
          }
        } else if (result.frozenExpired) {
          this.cashoutFreshData = null;
          this.cashoutBypassLoading = true;
          await this.calculateCashout({ code, bypassCache: true });
          const updated = this.getCashoutByCode(code);
          this.cashoutBypassLoading = false;
          if (updated.status === 'available') {
            this.cashoutFreshData = updated;
          } else {
            this.closeCashoutInline();
          }
        } else {
          this.closeCashoutInline();
          if (result.errorCodes && result.errorCodes.includes('CASHOUT_IS_ONLY_AVAILABLE_FOR_PLAYERS')) {
            this.setSnackbarText(this.$t('cashout.onlyForPlayers'));
            this.setSnackbar(true);
          }
          if (result.errorCodes && result.errorCodes.includes('BET_BUILDER_CASHOUT_NOT_ALLOWED')) {
            this.setSnackbarText(this.$t('cashout.betBuilderNotAvailable'));
            this.setSnackbar(true);
          }
        }
      } finally {
        this.cashoutExecuting = false;
      }
    },

    printBet(code) {
      if (this.getB2bEnabled) {
        this.$refs.printReceipt.triggerPrint();
        return;
      }
      if (this.getShortCode) {
        window.open(`${this.getPlatformSettings.print_endpoint}${code}&login=${this.getType}&ancestors=${this.getId}&type_odd=${this.getOddsType}`, '_blank', 'height=600,width=350');
      } else {
        window.open(`${this.getPlatformSettings.print_endpoint}${code}&type_odd=${this.getOddsType}`, '_blank', 'height=600,width=350');
      }
      if (this.getShortCode === true) {
        this.setReceiptPrinted({ betCode: code });
      } else {
        this.setReceiptPrinted({ code });
      }
    },
    download(code) {
      if (this.getB2bEnabled) {
        this.$refs.printReceipt.triggerDownload();
        return;
      }
      if (this.getShortCode) {
        window.open(`${this.getPlatformSettings.print_endpoint}${code}&login=${this.getType}&ancestors=${this.getId}&type_odd=${this.getOddsType}&autoPdf=true`, '_blank', 'height=600,width=350');
      } else {
        window.open(`${this.getPlatformSettings.print_endpoint}${code}&type_odd=${this.getOddsType}&autoPdf=true`, '_blank', 'height=600,width=350');
      }
    },

    // betFreeze (betFreezeDetailMixin hook): reload the list and show the fresh
    // bet in the open detail.
    async _reloadBetsAfterBetFreeze(code) {
      await this.initialize();
      const fresh = (this.getMyBets || []).find(b => b && b.code === code);
      if (fresh && this.dialog) {
        this.betToShow = fresh;
        this.setDetailMyBet(fresh);
      }
    },

    async openDetail(bet, fromAllBets = false) {
      this.detailFromAllBets = fromAllBets;
      this.betToShow = bet;
      this.showFullCode = false;
      this.detailLegsExpanded = true;
      this.cashoutModalInlineOpen = false;
      this.overlay_dialog_betlist_detail = true;
      this.dialog = true;
      this.setDetailMyBet(bet);
      this._onBetFreezeDetailOpened(bet);
      if (
          process.env.VUE_APP_CASHOUT_REFRESH_ON_OPEN === '1' &&
          bet.result === 'pending' &&
          this.isCashoutEligible(bet) &&
          bet.code &&
          this.isPlayerEligibleForCashout
      ) {
        this.calculateCashout({ code: bet.code });
      }
      try {
        await this._fetchAndAttachLiveScores(bet);
      } finally {
        this.overlay_dialog_betlist_detail = false;
      }
    },

    async _fetchAndAttachLiveScores(bet) {
      if (!bet || !bet.odds) return;

      const allOdds = bet.odds.map((odd, index) => ({ odd, index }));

      const eventIds = [...new Set(allOdds.map(({ odd }) => odd.event_source).filter(Boolean))];
      if (eventIds.length === 0) return;

      try {
        const res = await this.fetchLiveScores({ eventIds });

        const scoreMap = {};
        if (res && res.data) {
          res.data.forEach(item => {
            if (item.eventId == null) return;
            const sb = item.scoreboard;
            if (!sb || !sb.results) return;
            const homeResult = sb.results.find(r => String(r.position) === '1');
            const awayResult = sb.results.find(r => String(r.position) === '2');
            if (!homeResult || !awayResult) return;

            let timeActual = null;
            const clock = sb.clock;
            if (clock && clock.seconds != null && clock.seconds !== -1 && clock.seconds !== '-1') {
              timeActual = parseFloat(clock.seconds) / 60;
            }

            scoreMap[String(item.eventId)] = {
              home: homeResult.value,
              away: awayResult.value,
              timeActual,
            };
          });
        }

        allOdds.forEach(({ odd, index }) => {
          const score = scoreMap[String(odd.event_source)];
          if (!score) return;
          this.$set(this.betToShow.odds, index, {
            ...odd,
            result_actual_home: score.home,
            result_actual_away: score.away,
            time_actual: score.timeActual != null ? score.timeActual : -1,
            show_time: true,
          });
        });
      } catch (e) {
        console.error('[MyBets] fetchLiveScores error:', e);
      }
    },

    getBonus(bet) {
      switch (bet.type) {
        case 'single':
        case 'multiple':
          return parseFloat(bet._multiple_detail.bonus).toFixed(2) + ' ' + bet.currency;
        case 'integral':
          return parseFloat(bet._integral_detail.bonus).toFixed(2) + ' ' + bet.currency;
        case 'system':
          return parseFloat(bet._system_detail.bonus).toFixed(2) + ' ' + bet.currency;
      }
    },
    getWinning(bet) {
      switch (bet.type) {
        case 'single':
        case 'multiple':
          return this._pendingPotential(bet).toFixed(2) + ' ' + bet.currency;
        case 'integral':
          return parseFloat(bet._integral_detail.winning_total_max).toFixed(2) + ' ' + bet.currency;
        case 'system':
          return parseFloat(bet._system_detail.winning_all_total).toFixed(2) + ' ' + bet.currency;
      }
    },

    isTaxedBet(bet) {
      return bet && bet.percentageTaxValueOnWon && bet.percentageTaxValueOnWon > 0;
    },

    getWinningTaxed(bet) {
      switch (bet.type) {
        case 'single':
        case 'multiple':
          return parseFloat(bet._multiple_detail.expectedTotalTaxedWinnings).toFixed(2) + ' ' + bet.currency;
        case 'integral':
          return parseFloat(bet._integral_detail.winning_total_max_taxed).toFixed(2) + ' ' + bet.currency;
        case 'system':
          return parseFloat(bet._system_detail.winning_all_total_taxed).toFixed(2) + ' ' + bet.currency;
      }
    },

    getSpecifiers(spec) {
      if (!spec.includes('#')) return '';
      const split = spec.split('#');
      let result = '';
      if (split[1].includes(':')) {
        result = '(' + split[1] + ')';
      } else if (split[1].includes('(') && split[1].includes('.')) {
        result = ' [' + split[1] + '] ';
      } else if (split[1] !== '') {
        result = split[1];
      }
      return _.size(split) > 2 ? result + ' ' + split[2] : result;
    },

    getResult(result, team) {
      const position = team === 'home' ? '1' : '2';
      const point = _.find(result, { Position: position });
      return point ? +point.Value : 0;
    },

    async onShareBetImage() {
      if (this.loadingShareImage) return;
      this.loadingShareImage = true;
      await this.$refs.shareBetReceipt.triggerShare();
    },

    async rebet(odds) {
      const now = new Date();
      this.detailFromAllBets = false;
      this.dialog = false;
      await this.resetCart();
      await this.setOddsInCart(odds);
      await this.setCreatedAtCart(now.getTime());
      await this.refreshCart();
      await this.$root.$emit('sectionCart');
    },

    async calculateValueMyBets(bets) {
      const count_bets = _.size(bets);
      if (count_bets === 0) {
        this.mybets_total = 0;
        this.mybets_total_played = 0;
        this.mybets_winner = 0;
        this.mybets_void = 0;
        this.mybets_pending = 0;
        this.mybets_net = 0;
        return;
      }

      let total_played = 0, value_win = 0, value_void = 0, value_pending = 0, currency = '';

      this.mybets_total = count_bets;

      _.forEach(bets, bet => {
        currency = bet.currency;
        total_played += bet.amount_real;
        if (bet.result === 'win') value_win += bet.winning_real;
        if (bet.result === 'void') value_void += bet.amount_real;
        if (bet.result === 'pending') {
          if (bet.type === 'multiple' || bet.type === 'single') value_pending += this._pendingPotential(bet);
          if (bet.type === 'integral') value_pending += bet._integral_detail.winning_total_max;
          if (bet.type === 'system') value_pending += bet._system_detail.winning_all_total;
        }
      });

      this.mybets_total_played = total_played;
      this.mybets_winner = value_win;
      this.mybets_void = value_void;
      this.mybets_pending = value_pending;
      this.mybets_net = value_win - (total_played - value_void);
      this.mybets_currency = currency;
    },

    formatPrice(value) {
      return (value / 1).toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    },

    async allMyBets() {
      this.overlay = true;
      try {
        await this.searchBets();
        await this.calculateValueMyBets(this.getMyBetsAll);
        this.dialog_betlist = true;
        await new Promise(r => setTimeout(r, 100));
        window.scrollTo({ top: 0, behavior: 'smooth' })
      } finally {
        this.overlay = false;
      }
    },

    async searchBets() {
      this.overlay_dialog_betlist = true;
      try {
        const criteria = {
          ...(this.filterType !== '') && { type: this.filterType },
          ...(this.filterResult !== '') && { result: this.filterResult },
          ...(this.filterPayed !== '') && { payed: this.filterPayed },
          ...(this.filterCode !== '') && { code: this.filterCode },
          ...(this.filterBetCode !== '') && { betCode: this.filterBetCode },
          ...(this.filterContext !== '') && { bet_context: this.filterContext },
          ...(this.filterFromTime !== '') && { fromTime: new Date(this.filterFromTime).getTime() },
          ...(this.filterToTime !== '') && { toTime: new Date(this.filterToTime).getTime() + 86399000 },
        };

        const isAgencyOrCashier = (this.getUserType != null && this.getUserType == 4) || (this.getCashier != null && this.getCashier === true);
        if (isAgencyOrCashier) criteria.onlyPlayedByMe = this.showOnlyMyBets;

        await this.fetchMyBetsAllWithoutCancelled(criteria);
        await this.calculateValueMyBets(this.getMyBetsAll);
      } finally {
        this.overlay_dialog_betlist = false;
      }
    },

    setResultFilter(value) {
      this.filterResult = value;
      this.searchBets();
    },

    setPeriod(days) {
      const to = new Date();
      const from = new Date();
      from.setDate(from.getDate() - days);
      this.filterToTime = to.toISOString().substr(0, 10);
      this.filterFromTime = from.toISOString().substr(0, 10);
      this.searchBets();
    },

    async resetFilter() {
      this.filterType = '';
      this.filterResult = '';
      this.filterPayed = '';
      this.filterCode = '';
      this.filterBetCode = '';
      this.filterContext = '';
      this.filterFromTime = new Date(new Date(new Date()).setMonth(new Date(new Date()).getMonth() - 1)).toISOString().substr(0, 10);
      this.filterToTime = new Date(new Date()).toISOString().substr(0, 10);
      await this.searchBets();
    },

    async handleClick(value) {
      this.dialog_betlist = false;
      this.openDetail(value, true);
    },

    // Chiude il dettaglio; se arrivava da "Tutte le scommesse" ci torna
    // (filtri e risultati sono ancora nello stato del componente).
    closeDetail(backToList) {
      const reopenList = backToList && this.detailFromAllBets;
      this.detailFromAllBets = false;
      this.dialog = false;
      if (reopenList) this.dialog_betlist = true;
    },

    async handleKeyDown(e) {
      if (e.code === 'Enter' || e.code === 'NumpadEnter') {
        await this.searchBets();
      }
    },
  },

  async created() {
    this._cashoutPollTimer = null;
    this._cashoutTemporaryTimer = null;
    // Non-reactive on purpose — nothing renders from them. Both would be
    // undefined (falsy) anyway on a fresh instance, but the cart tabs mount and
    // destroy this component on every switch, so the reset is spelled out next
    // to the timers rather than left implicit.
    this._cashoutDestroyed = false;
    this._cashoutRunActive = false;
    this._cashoutSleeper = createSleeper();
    // betFreeze flags and timers live in the betFreeze mixins (their hooks run first).
    this.initialize();
  },

  mounted() {
    this.mountDialogLayer();
  },

  beforeDestroy() {
    // Set BEFORE clearing the timers: a pool may already be mid-run, and its
    // workers check this flag before every task so they stop pulling instead of
    // firing requests for a panel that no longer exists.
    this._cashoutDestroyed = true;
    // Resolves every pending pause at once: workers wake, see the flag, stop.
    if (this._cashoutSleeper) this._cashoutSleeper.cancelAll();
    this._stopCashoutPoller();
    this._stopTemporaryPoller();
    document.body.style.overflow = '';
    this.$root.$emit('bet-detail-open', false);
    // Il layer vive fuori dal root del componente: Vue non lo rimuove da solo.
    if (this._dialogLayer && this._dialogLayer.parentNode) {
      this._dialogLayer.parentNode.removeChild(this._dialogLayer);
    }
  },
};
</script>

<style scoped>
/* ===========================
   MAIN CONTAINER
=========================== */
#my-bets-component {
  padding: 0;
  font-size: var(--font-size-3xl);
}

/* ===========================
   GLOBAL OVERLAY + SPINNERS
=========================== */
.global-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.65);
  display: flex;
  align-items: center;
  justify-content: center;
}

.spinner-lg {
  width: 48px;
  height: 48px;
  border: 4px solid rgba(255, 255, 255, 0.15);
  border-top-color: var(--color-primary, #fed430);
  border-radius: 50%;
  animation: spin 0.75s linear infinite;
}

.spin-sm {
  width: 12px;
  height: 12px;
  border: 2px solid rgba(255, 255, 255, 0.15);
  border-top-color: var(--color-primary, #fed430);
  border-radius: 50%;
  animation: spin 0.65s linear infinite;
  flex-shrink: 0;
}

@keyframes spin { to { transform: rotate(360deg); } }

/* ===========================
   SKELETON LOADER
=========================== */
.skeleton-list {
  padding: 4px 6px;
}

.skel-bet {
  padding: 8px 10px;
  margin: 3px 0;
  border-radius: 5px;
  background: rgba(255, 255, 255, 0.03);
  border-left: 3px solid rgba(255, 255, 255, 0.06);
}

.skel-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.skel-blob {
  height: 8px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.1);
  animation: skel-pulse 1.5s ease-in-out infinite;
}

.skel-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  animation: skel-pulse 1.5s ease-in-out infinite;
  flex-shrink: 0;
  margin-left: auto;
}

@keyframes skel-pulse {
  0%, 100% { opacity: 0.6; }
  50%       { opacity: 0.2; }
}

/* ===========================
   EMPTY / MESSAGE STATES
=========================== */
.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
}

.empty-state__card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 24px 20px;
  width: 100%;
  border-radius: 12px;
  text-align: center;
  animation: fadeInUp 0.4s ease;
}

@keyframes fadeInUp {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.empty-state__icon-wrap {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: rgba(255,255,255,0.07);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-op-50-platform);
  flex-shrink: 0;
}

.empty-state__icon-wrap--lock {
  background: rgba(254, 212, 48, 0.1);
  color: rgba(254, 212, 48, 0.6);
}

.empty-state__title {
  font-size: var(--font-size-6xl);
  font-weight: 600;
  color: var(--text-platform);
}

.empty-state__sub {
  font-size: var(--font-size-3xl);
  color: var(--text-op-50-platform);
  line-height: 1.4;
}

.section-header { padding: 8px 10px 4px; }
.section-label {
  font-size: var(--font-size-md);
  font-weight: 700;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.35);
}

/* ===========================
   BET LIST ITEM
=========================== */
.single-bet {
  display: flex;
  align-items: flex-start;
  padding: 10px 12px 10px 14px;
  margin: 0 0 8px;
  border-radius: 10px;
  cursor: pointer;
  border: 1px solid var(--border-color, rgba(255,255,255,0.06));
  border-left: 3px solid rgba(255, 255, 255, 0.1);
  background: var(--bg-card);
  transition: background 0.15s ease, transform 0.12s ease;
}
@media (hover: hover) { .single-bet:hover { background: rgba(255, 255, 255, 0.07); } }
.single-bet:active { transform: scale(0.99); }
.single-bet--pending { border-left-color: #ff9800; }
.single-bet--win     { border-left-color: #4caf50; }
.single-bet--lose    { border-left-color: #f44336; }
.single-bet--void    { border-left-color: #2196f3; }

.bet-info { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 4px; }
.bet-row { display: flex; align-items: center; justify-content: space-between; gap: 8px; width: 100%; }

.bet-type {
  min-width: 0;
  font-size: var(--font-size-3xl);
  font-weight: 700;
  text-transform: capitalize;
  color: var(--text-platform);
  display: flex;
  align-items: center;
  gap: 5px;
}

.bet-event-count { font-weight: 700; color: var(--text-platform); }

.bet-type-name, .bet-event-count { flex-shrink: 0; }
.bet-type .bet-date { min-width: 0; overflow: hidden; text-overflow: ellipsis; font-weight: 400; text-transform: none; }
.bet-money { display: inline-flex; align-items: baseline; gap: 4px; min-width: 0; font-size: var(--font-size-2xl); white-space: nowrap; }
.bet-money-label { color: var(--text-op-50-platform); }
.bet-money-amount { color: var(--text-platform); font-weight: 700; }
.bet-money-win { font-weight: 800; color: var(--text-platform); }
.bet-money-win--pending,
.bet-money-win--win { color: var(--win); }
.bet-money-win--lose { color: var(--text-op-50-platform); text-decoration: line-through; font-weight: 600; }

.badge-live {
  font-size: var(--font-size-xs); font-weight: 700; text-transform: uppercase; background: #f44336; color: #fff; padding: 1px 5px; border-radius: 2px; letter-spacing: 0.5px; line-height: 1.4;
}

/* Liste: scommessa giocata live indicata solo con l'icona (nel dettaglio resta il badge testuale) */
.icon-live {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: var(--live);
}
.icon-live .v-icon {
  color: #fff !important;
  font-size: 13px !important;
}

/* Dettaglio: scommessa giocata con freebet (palette rosa/arancio delle freebet) */
.badge-freebet {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  max-width: 160px;
  padding: 1px 7px 1px 5px;
  border-radius: 10px;
  background: linear-gradient(90deg, #ff4d8d, #ff8a3d);
  color: #fff;
  font-size: var(--font-size-xs);
  font-weight: 800;
  text-transform: uppercase;
  line-height: 1.6;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.badge-freebet .v-icon {
  color: #fff !important;
  font-size: 12px !important;
}

.badge-cashout {
  display: inline-flex; align-items: center; gap: 3px; background: rgba(76,175,80,0.2); color: #4caf50; vertical-align: middle; font-size: var(--font-size-xs); font-weight: 800; text-transform: uppercase; padding: 3px 8px; border-radius: 999px; flex-shrink: 0;
}
.text-cashout {
  display: inline-flex; align-items: center; gap: 3px; font-size: var(--font-size-xl); font-weight: 800; text-transform: capitalize; color: #f9a825; vertical-align: middle;
}

.badge-odd-live {
  display: inline-flex; align-items: center; gap: 4px; font-size: var(--font-size-xs); font-weight: 800; text-transform: uppercase; letter-spacing: 0.8px; color: #fff; background: linear-gradient(135deg, #d32f2f, #f44336); padding: 2px 6px 2px 4px; border-radius: 3px; margin-right: 5px; vertical-align: middle; box-shadow: 0 0 6px rgba(244, 67, 54, 0.5);
}
.badge-odd-live__dot { width: 6px; height: 6px; border-radius: 50%; background: #fff; flex-shrink: 0; animation: pulse-live-dot 1.2s ease-in-out infinite; }

@keyframes pulse-live-dot { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.3; transform: scale(0.6); } }

.bet-date { font-size: var(--font-size-xl); color: var(--text-op-50-platform); white-space: nowrap; }

.bet-status-badge { font-size: var(--font-size-xs); font-weight: 800; text-transform: uppercase; padding: 3px 8px; border-radius: 999px; flex-shrink: 0; }
.bet-status-badge--pending { background: rgba(255,152,0,0.2);  color: #ff9800; }
.bet-status-badge--win     { background: rgba(76,175,80,0.2);  color: #4caf50; }
.bet-status-badge--lose    { background: rgba(244,67,54,0.2);  color: #f44336; }
.bet-status-badge--void    { background: rgba(33,150,243,0.2); color: #2196f3; }

.bet-status-icon { display: flex; align-items: center; flex-shrink: 0; }

/* ===========================
   RESULT ICONS (SVG)
=========================== */
.result-svg { width: 16px; height: 16px; flex-shrink: 0; }
.result-svg--win      { fill: var(--win); }
.result-svg--lose     { fill: var(--lose); }
.result-svg--void     { fill: #2196f3; }
.result-svg--pending  { stroke: var(--text-op-50-platform, rgba(255,255,255,0.4)); fill: none; }
.result-svg--cancelled{ fill: #fff; }

.ctx-badge { font-size: var(--font-size-md); font-weight: 700; text-transform: uppercase; padding: 2px 6px; border-radius: 3px; letter-spacing: 0.5px; }
.ctx-badge--live     { background: #f44336; color: #fff; }
.ctx-badge--antepost { background: #7b1fa2; color: #fff; }

.circle-pending { width: 10px; height: 10px; border-radius: 50%; background: var(--pending); animation: pulse-pending 1.4s ease-in-out infinite; flex-shrink: 0; }
@keyframes pulse-pending { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.45; transform: scale(0.75); } }

.result-icon { display: inline-flex; align-items: center; gap: 4px; }
.result-cell { display: flex; align-items: center; }

/* ===========================
   LOAD MORE
=========================== */
.load-more-row { padding: 8px 6px; }

/* ===========================
   NATIVE DIALOG BUTTONS
=========================== */
.dialog-close-btn {
  height: 32px; padding: 0 16px; color: var(--btn-background-second); border: 1px solid var(--btn-background-second); border-radius: 6px; font-size: var(--font-size-2xl); font-weight: 600; cursor: pointer; transition: background 0.15s; width: 50%; background: transparent;
}
.dialog-close-btn:hover { background: rgba(255,255,255,0.05); }

/* ===========================
   BLINK ANIMATION
=========================== */
.blink_me { animation: blinker 1s step-start infinite; }
@keyframes blinker { 50% { opacity: 0; } }

/* ===========================
   CASHOUT
=========================== */
.bet-row--cashout { margin-top: 6px; justify-content: flex-start; min-height: 20px; }
.cashout-bb-note { font-size: 12px; font-style: italic; opacity: 0.75; }
.bet-row--cashout .cashout-btn { height: 34px; border-radius: 8px; font-size: var(--font-size-2xl); }

/* Cashout disponibile: azione principale, deve attirare l'occhio */
.cashout-btn.cashout-btn--hero {
  position: relative;
  overflow: hidden;
  justify-content: space-between;
  height: 42px;
  padding: 0 6px 0 12px;
  border-radius: 10px;
  font-size: var(--font-size-3xl);
  font-weight: 800;
  text-transform: uppercase;
  box-shadow: 0 8px 20px -10px var(--btn-background), inset 0 1px 0 rgba(255, 255, 255, 0.18);
  transition: transform 0.12s ease, filter 0.15s;
}
.cashout-btn.cashout-btn--hero:active { transform: scale(0.98); }
/* Riflesso che attraversa il bottone ogni pochi secondi */
.cashout-btn.cashout-btn--hero::after {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  left: -60%;
  width: 50%;
  background: linear-gradient(110deg, transparent 0%, rgba(255, 255, 255, 0.28) 50%, transparent 100%);
  transform: skewX(-20deg);
  animation: cashout-shine 3.2s ease-in-out infinite;
  pointer-events: none;
}
@keyframes cashout-shine {
  0%, 55% { left: -60%; }
  100%    { left: 130%; }
}
.cashout-btn__label { display: inline-flex; align-items: center; gap: 6px; min-width: 0; }
.cashout-btn__icon { color: inherit !important; }
.cashout-btn__live {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #f44336;
  box-shadow: 0 0 0 0 rgba(244, 67, 54, 0.6);
  animation: cashout-live-pulse 1.4s ease-out infinite;
}
@keyframes cashout-live-pulse {
  0%   { box-shadow: 0 0 0 0 rgba(244, 67, 54, 0.6); }
  100% { box-shadow: 0 0 0 7px rgba(244, 67, 54, 0); }
}
.cashout-btn__amount {
  flex-shrink: 0;
  padding: 5px 10px;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.18);
  font-size: var(--font-size-4xl);
  font-weight: 800;
  text-transform: none;
}
.cashout-btn__amount small { font-size: var(--font-size-xl); font-weight: 700; }
@media (prefers-reduced-motion: reduce) {
  .cashout-btn.cashout-btn--hero::after,
  .cashout-btn__live { animation: none; }
}
.cashout-btn {
  display: inline-flex; align-items: center; justify-content: center; height: 32px; padding: 0 8px; font-size: var(--font-size-3xl); font-weight: 700; background: var(--btn-background); color: var(--btn-text); border-radius: 6px; cursor: pointer; transition: filter 0.15s; white-space: nowrap; border: 1px solid transparent; width: 100%; gap: 6px;
}
.cashout-btn:hover:not(:disabled) { filter: brightness(1.1); }
.cashout-btn--suspended { background: transparent!important; border-color: var(--btn-background-second); color: var(--btn-background-second); opacity: 0.5; animation: cashout-suspended-pulse 2.4s ease-in-out infinite; }
@keyframes cashout-suspended-pulse { 0%, 100% { opacity: 0.65; } 50% { opacity: 0.5;  } }
.cashout-btn--temporary { background-color: rgba(255, 152, 0, 0.18) !important; color: rgba(255, 152, 0, 0.7) !important; animation: cashout-temporary-pulse 2s ease-in-out infinite; border: none; }
@keyframes cashout-temporary-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.55; } }
.cashout-live-badge { font-size: var(--font-size-xxs); font-weight: 800; background: #f44336; color: #fff; padding: 1px 4px; border-radius: 2px; margin-left: 4px; letter-spacing: 0.4px; }
.cashout-inline-panel { padding: 12px 0 0 0; border-radius: 0 0 6px 6px; }
.cashout-inline-actions { display: flex; align-items: center; justify-content: space-between; margin-top: 4px; gap: 8px; }
.cashout-expired-notice { color: #ff9800; font-size: var(--font-size-3xl); line-height: 1.4; }
.cashout-confirm-body { font-size: var(--font-size-2xl); line-height: 1.6; }
.cashout-verifying { display: flex; align-items: center; font-size: var(--font-size-4xl); color: var(--text-platform); padding: 8px 0; }
.cashout-confirm-amount { font-size: var(--font-size-4xl); color: var(--color-secondary); }
.cashout-partial-won { color: rgba(255, 255, 255, 0.6); }

.cashout-confirm-btn {
  display: inline-flex; align-items: center; justify-content: center; height: 32px; padding: 0; background: var(--btn-background-second); color: var(--btn-text); border: none; border-radius: 6px; font-size: var(--font-size-3xl); font-weight: 700; cursor: pointer; transition: filter 0.15s; min-width: 80px; width: 50%;
}
.cashout-confirm-btn:hover:not(:disabled) { filter: brightness(1.1); }
.cashout-confirm-btn:disabled { opacity: 0.6; cursor: default; }

.btn-hourglass { vertical-align: middle; margin-right: 5px; flex-shrink: 0; animation: hourglass-flip 1.2s ease-in-out infinite; transform-origin: center; }
@keyframes hourglass-flip { 0%   { transform: rotate(0deg); } 45%  { transform: rotate(0deg); } 55%  { transform: rotate(180deg); } 100% { transform: rotate(180deg); } }
.btn-spinner-inline { width: 14px; height: 14px; border: 2px solid rgba(0,0,0,0.2); border-top-color: #000; border-radius: 50%; animation: spin 0.65s linear infinite; display: inline-block; }

/* ── Ticket Detail Dialog & Modals ────────────────────────────────── */
.td-overlay-backdrop {
  position: fixed; inset: 0; z-index: 9999; background: rgba(0,0,0,0.75); display: flex; align-items: center; justify-content: center;
}

.td-card {
  position: relative; width: 440px; max-width: calc(100vw - 32px); max-height: 75vh; background: var(--sidebar); border-radius: 10px; overflow: hidden; display: flex; flex-direction: column; box-shadow: 0 24px 64px rgba(0,0,0,0.6);
}

.all-bets-card {
  position: relative; width: 640px; max-width: calc(100vw - 32px); max-height: 90vh; height: 90vh; background: var(--sidebar); border: 1px solid var(--border-color); border-radius: 14px; overflow: hidden; display: flex; flex-direction: column; box-shadow: 0 24px 64px rgba(0,0,0,0.5);
}

@media (max-width: 800px) {
  .td-overlay-backdrop { align-items: flex-end; }
  .td-card { width: 100%; max-width: 100%; height: 100dvh; max-height: 100dvh; border-radius: 0; }
  .all-bets-card { width: 100vw; height: 100dvh; max-height: 100dvh; max-width: 100vw; border-radius: 0; }
  .td-scroll-body { padding-bottom: 80px; }

  .cashout-btn {
    height: 40px; font-size: var(--font-size-4xl); text-transform: uppercase;
  }

}

.td-backdrop-fade-enter-active { transition: opacity 0.25s ease; }
.td-backdrop-fade-leave-active { transition: opacity 0.2s ease; }
.td-backdrop-fade-enter, .td-backdrop-fade-leave-to { opacity: 0; }
.td-sheet-enter-active { transition: transform 0.3s cubic-bezier(0.32,0.72,0,1), opacity 0.25s ease; }
.td-sheet-leave-active { transition: transform 0.2s cubic-bezier(0.32,0.72,0,1), opacity 0.2s ease; }
.td-sheet-enter, .td-sheet-leave-to { opacity: 0; transform: scale(0.96) translateY(8px); }
@media (max-width: 600px) { .td-sheet-enter, .td-sheet-leave-to { opacity: 1; transform: translateY(100%); } }

.td-overlay { position: absolute; inset: 0; background: rgba(0,0,0,0.65); display: flex; align-items: center; justify-content: center; z-index: 10; }
.td-overlay-absolute { z-index: 999; }

.td-icon-btn .v-icon{
  color: var(--text-op-50-platform)!important;
  font-size: var(--font-size-8xl) !important;
}
.td-icon-btn:hover { color: var(--text-platform); background: rgba(255,255,255,0.08); }
.td-code-block { flex: 1; display: flex; align-items: center; gap: 5px; min-width: 0; }
.td-code-full { font-family: monospace; font-size: var(--font-size-md); color: var(--text-op-50-platform); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.td-topbar-right { display: flex; align-items: center; gap: 4px; flex-shrink: 0; }


.td-scroll-body { flex: 1; overflow-y: auto; display: flex; flex-direction: column; }

.td-result-badge--pending { background: #ff9800; }
.td-result-badge--win     { background: #4caf50; color: #fff;}
.td-result-badge--lose    { background: #f44336; color: #fff; }
.td-result-badge--void    { background: #2196f3; color: #fff;}



/* ── Topbar ── */
.td-topbar {
  display: flex; align-items: center; gap: 8px; padding: 12px 12px 12px 16px; flex-shrink: 0; background: var(--sidebar);
  border-bottom: 1px solid var(--border-color, rgba(255,255,255,0.07));
}
.td-icon-btn {
  width: 34px; height: 34px; background: transparent; border: none; color: var(--text-op-50-platform); cursor: pointer;
  display: flex; align-items: center; justify-content: center; padding: 0; border-radius: 10px; transition: color 0.15s, background 0.15s;
}
.td-back-btn { margin-left: -8px; color: var(--text-platform); }
.td-code { font-family: monospace; font-size: var(--font-size-3xl); font-weight: 600; color: var(--text-platform); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

/* ── Hero: stato, importi, cashout ── */
.td-hero {
  margin: 14px 14px 0; padding: 16px; border-radius: 14px; background: var(--bg-card);
  border: 1px solid var(--border-color, rgba(255,255,255,0.06));
  display: flex; flex-direction: column; gap: 12px;
}
.td-hero-head { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
.td-hero-title { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; min-width: 0; }
.td-type { font-size: var(--font-size-6xl); font-weight: 800; text-transform: capitalize; color: var(--text-platform); }
.td-hero-count { font-size: var(--font-size-2xl); color: var(--text-op-50-platform); }
.td-hero-date { display: flex; align-items: center; gap: 8px; margin-top: -6px; font-size: var(--font-size-xl); color: var(--text-op-50-platform); }

.td-result-badge { flex-shrink: 0; font-size: var(--font-size-md); font-weight: 800; text-transform: uppercase; padding: 4px 10px; border-radius: 999px; color: #000; }

.td-hero-stats { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
.td-stat {
  display: flex; flex-direction: column; gap: 4px; padding: 12px; border-radius: 10px; background: var(--sidebar);
}
.td-stat-label { font-size: var(--font-size-xl); color: var(--text-op-50-platform); }
.td-stat-value { font-size: var(--font-size-6xl); font-weight: 800; color: var(--text-platform); white-space: nowrap; }
.td-stat-value small { font-size: var(--font-size-xl); font-weight: 700; }
.td-stat--accent .td-stat-value { color: var(--color-primary-light); }

.td-hero-rows { display: flex; flex-direction: column; gap: 6px; padding: 0 2px; }
.td-hero-row { display: flex; justify-content: space-between; align-items: center; font-size: var(--font-size-2xl); color: var(--text-op-50-platform); }
.td-hero-row span:last-child { font-weight: 700; color: var(--text-platform); }
.td-hero-row .td-bonus-before { margin-right: 6px; font-weight: 400; color: var(--text-op-50-platform); }

.td-hero-cashout { padding-top: 12px; border-top: 1px solid var(--border-color, rgba(255,255,255,0.06)); }
.td-cashout-area { display: flex; flex-direction: column; min-width: 0; }
.td-cashout-btn { width: 100%; height: 50px !important; border-radius: 12px !important; padding: 0 7px 0 14px !important; font-size: var(--font-size-4xl) !important; font-weight: 800 !important; }
.td-cashout-btn .cashout-btn__amount { padding: 7px 12px; font-size: var(--font-size-5xl); }
.td-cashout-panel { background: var(--sidebar); border: 1px solid var(--border-color, rgba(255,255,255,0.1)); border-radius: 12px; padding: 14px; }

/* ── Eventi ── */
.td-events { padding: 18px 14px 16px; }
.td-events-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; }
.td-events-title { display: flex; align-items: center; gap: 8px; font-size: var(--font-size-3xl); font-weight: 800; text-transform: uppercase; color: var(--text-platform); }
.td-events-count {
  min-width: 20px; height: 20px; padding: 0 6px; border-radius: 10px; display: inline-flex; align-items: center; justify-content: center;
  background: var(--color-primary-op, rgba(255,255,255,0.08)); color: var(--color-primary-light); font-size: var(--font-size-xl);
}
.td-toggle-btn { background: transparent; border: none; color: var(--text-op-50-platform); font-size: var(--font-size-2xl); cursor: pointer; display: flex; align-items: center; gap: 3px; padding: 4px 0; }
.td-toggle-btn:hover { color: var(--text-platform); }

.td-legs { display: flex; flex-direction: column; gap: 10px; }
.td-leg {
  background: var(--bg-card); border-radius: 12px; overflow: hidden;
  border: 1px solid var(--border-color, rgba(255,255,255,0.06));
}

.td-leg-match { position: relative; display: flex; align-items: flex-start; gap: 10px; padding: 12px 14px 10px; }
.td-leg-match::after {
  content: ''; position: absolute; left: 14px; right: 14px; bottom: 0; height: 1px; pointer-events: none;
  background: linear-gradient(90deg, var(--text-op-50-platform) 0%, transparent 85%); opacity: 0.5;
}
.td-leg-match-body { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 3px; }
.td-leg-teams {
  font-size: var(--font-size-2xl); font-weight: 600; line-height: 1.35; color: var(--text-platform);
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}
.td-leg-vs { margin: 0 6px; font-weight: 400; color: var(--text-op-50-platform); }
.td-leg-meta { display: flex; align-items: center; gap: 5px; min-width: 0; font-size: var(--font-size-xl); color: var(--text-op-50-platform); white-space: nowrap; }
.td-leg-cat { min-width: 0; overflow: hidden; text-overflow: ellipsis; }
.td-leg-time { flex-shrink: 0; }
.td-leg-result-icon { display: flex; align-items: center; flex-shrink: 0; padding-top: 1px; }

.td-leg-pick { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 10px 14px 12px; }
.td-leg-pick-main { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.td-leg-outcome { font-size: var(--font-size-4xl); font-weight: 700; color: var(--text-platform); }
.td-leg-market-name { font-size: var(--font-size-xl); color: var(--text-op-50-platform); }
.td-leg-odd {
  flex-shrink: 0; min-width: 54px; padding: 7px 10px; border-radius: 10px; text-align: center;
  background: var(--sidebar); border: 1px solid var(--border-color, rgba(255,255,255,0.08));
  font-size: var(--font-size-4xl); font-weight: 800; color: var(--color-accent);
}
.td-leg--win .td-leg-odd  { color: var(--win, var(--color-accent)); }
.td-leg--lose .td-leg-odd { color: var(--text-op-50-platform); text-decoration: line-through; }

.td-leg-live-score { display: flex; align-items: center; gap: 8px; padding: 0 14px 12px; font-size: var(--font-size-xl); color: var(--text-platform); }
.td-leg-live-score .result {
  padding: 2px 8px; border-radius: 6px; background: var(--sidebar);
  color: var(--color-secondary); font-size: var(--font-size-2xl); font-weight: 800;
}
.td-leg-clock { display: inline-flex; align-items: center; gap: 3px; color: var(--text-op-50-platform); }

/* Bet Builder: stessa card, struttura a timeline */
.td-leg--bb .td-leg-summary { display: flex; align-items: center; justify-content: space-between; gap: 8px; padding: 12px 14px 10px; }
.td-leg--bb .td-leg-event { display: flex; align-items: center; gap: 6px; min-width: 0; }
.td-leg--bb .td-leg-event-name { font-size: var(--font-size-2xl); font-weight: 600; color: var(--text-platform); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.td-leg--bb .td-leg-detail { padding: 8px 14px 12px; border-top: 1px solid var(--border-color, rgba(255,255,255,0.06)); }
.td-leg--bb .td-leg-meta { justify-content: space-between; }
.td-leg--bb .td-leg-odd { color: var(--color-primary-light); }
.td-leg--bb .td-leg-live-score { padding: 4px 0 0; }

/* ── BetBuilder badge nella leg ── */
.bb-badge-leg {
  flex-shrink: 0;
  padding: 2px 5px;
  border-radius: 4px;
  font-size: var(--font-size-xs);
  font-weight: 800;
  line-height: 13px;
  color: var(--bg-body);
  background: var(--color-primary-light);
}

/* ── BB Timeline nella modal ── */
.td-bb-timeline {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 6px 0 4px 16px;
  position: relative;
  margin-top: 6px;
}
.td-bb-row {
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 1px;
}
.td-bb-row:not(.td-bb-row--last)::before {
  content: '';
  position: absolute;
  left: -9px;
  top: 5px;
  bottom: -8px;
  width: 1px;
  background: var(--color-primary-light, #21a8f7);
  opacity: 0.4;
}
.td-bb-dot {
  position: absolute;
  left: -12px;
  top: 4px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--color-primary-light, #21a8f7);
  box-shadow: 0 0 4px rgba(33, 168, 247, 0.6);
  z-index: 1;
}
.td-bb-content { display: flex; flex-direction: column; gap: 1px; flex: 1; min-width: 0; }
.td-bb-result-icon { display: flex; align-items: center; flex-shrink: 0; margin-left: auto; }
.td-bb-outcome { font-size: var(--font-size-xl); font-weight: 600; color: var(--text-platform); }
.td-bb-market { font-size: var(--font-size-md); color: var(--text-op-50-platform); text-transform: uppercase; }
.td-bb-combo-row {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 4px;
  padding-top: 6px;
  border-top: 1px solid rgba(255,255,255,0.07);
}
.td-bb-combined-odd {
  color: var(--color-primary-light) !important;
  font-size: var(--font-size-3xl) !important;
}




/* ── All Bets Dialog ─────────────────────────────────────────── */
.all-bets-body {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  overscroll-behavior: contain;
}

.ab-title { font-size: var(--font-size-6xl); font-weight: 800; color: var(--text-platform); }
.ab-count {
  min-width: 22px; height: 22px; padding: 0 7px; border-radius: 11px; display: inline-flex; align-items: center; justify-content: center;
  background: var(--color-primary-op, rgba(255,255,255,0.08)); color: var(--color-primary-light); font-size: var(--font-size-xl); font-weight: 800;
}

/* Riepilogo */
.ab-summary {
  padding: 16px; border-radius: 14px; background: var(--bg-card);
  border: 1px solid var(--border-color, rgba(255,255,255,0.06));
  display: flex; flex-direction: column; gap: 14px;
}
.ab-net { display: flex; flex-direction: column; gap: 2px; }
.ab-net-label { font-size: var(--font-size-2xl); color: var(--text-op-50-platform); text-transform: capitalize; }
.ab-net-value { font-size: var(--font-size-11xl); font-weight: 800; line-height: 1.1; color: var(--text-platform); }
.ab-net-value small { font-size: var(--font-size-3xl); font-weight: 700; }
.ab-net-value.is-positive { color: var(--win); }
.ab-net-value.is-negative { color: var(--lose, #f44336); }
.ab-stats { display: grid; grid-template-columns: repeat(auto-fit, minmax(96px, 1fr)); gap: 8px; }
.ab-stat { display: flex; flex-direction: column; justify-content: space-between; gap: 3px; padding: 10px; border-radius: 10px; background: var(--sidebar); min-width: 0; }
.ab-stat-label { font-size: var(--font-size-xl); line-height: 1.25; color: var(--text-op-50-platform); text-transform: capitalize; }
.ab-stat-value { font-size: var(--font-size-4xl); font-weight: 800; color: var(--text-platform); white-space: nowrap; }
.ab-stat-value.is-win { color: var(--win); }
.ab-stat-value.is-pending { color: #ff9800; }

/* Filtri */
.ab-filters { display: flex; flex-direction: column; gap: 10px; }
.ab-chips { display: flex; gap: 6px; overflow-x: auto; scrollbar-width: none; margin: 0 -14px; padding: 0 14px; }
.ab-chips::-webkit-scrollbar { display: none; }
.ab-chip {
  flex-shrink: 0; height: 34px; padding: 0 14px; border-radius: 999px; cursor: pointer;
  border: 1px solid var(--border-color, rgba(255,255,255,0.1)); background: var(--bg-card);
  color: var(--text-op-50-platform); font-size: var(--font-size-2xl); font-weight: 600; text-transform: capitalize;
  transition: color 0.15s, border-color 0.15s, background 0.15s;
}
.ab-chip.active { color: var(--text-platform); border-color: var(--color-primary-light); background: var(--color-primary-op, rgba(255,255,255,0.06)); }
.ab-chip--sm { height: 30px; padding: 0 12px; font-size: var(--font-size-xl); }
.ab-chips--period { margin: 0; padding: 0; }

.ab-period { display: flex; flex-direction: column; gap: 8px; }
.ab-dates { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
.ab-date { display: flex; flex-direction: column; gap: 4px; font-size: var(--font-size-xl); color: var(--text-op-50-platform); }

.ab-more-toggle {
  display: flex; align-items: center; gap: 6px; align-self: flex-start; padding: 6px 0;
  background: transparent; border: none; cursor: pointer; color: var(--text-platform); font-size: var(--font-size-2xl); font-weight: 600;
}
.ab-more-toggle .v-icon { color: inherit !important; }
.ab-more-count {
  min-width: 18px; height: 18px; padding: 0 5px; border-radius: 9px; display: inline-flex; align-items: center; justify-content: center;
  background: var(--color-primary-light); color: var(--bg-body); font-size: var(--font-size-md); font-weight: 800;
}
.ab-more-chevron { transition: transform 0.2s; color: var(--text-op-50-platform); }
.ab-more-chevron.open { transform: rotate(180deg); }

.ab-advanced {
  display: flex; flex-direction: column; gap: 12px; padding: 14px; border-radius: 12px;
  background: var(--bg-card); border: 1px solid var(--border-color, rgba(255,255,255,0.06));
}

.filter-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); gap: 10px; }
.filter-col { display: flex; flex-direction: column; gap: 4px; }
.filter-label { font-size: var(--font-size-xl); color: var(--text-op-50-platform); font-weight: 600; text-transform: capitalize; }

.custom-input, .custom-select {
  width: 100%;
  height: 38px;
  background: var(--sidebar);
  border: 1px solid var(--border-color);
  color: var(--text-platform);
  border-radius: 10px;
  padding: 0 11px;
  font-size: var(--font-size-2xl);
  outline: none;
  transition: border-color 0.2s;
  appearance: none;
}
.custom-input:focus, .custom-select:focus { border-color: var(--color-primary-light); }
.custom-date { color-scheme: dark; background: var(--bg-card); }
.custom-select {
  background-image: url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23ffffff55%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E");
  background-repeat: no-repeat;
  background-position: right 11px top 50%;
  background-size: 9px auto;
  padding-right: 28px;
}
.custom-select option { background: var(--bg-card, #1e1e1e); color: var(--text-platform); }

.custom-radio-group { display: flex; gap: 14px; flex-wrap: wrap; }
.custom-radio {
  display: flex; align-items: center; gap: 7px; cursor: pointer; font-size: var(--font-size-2xl); color: var(--text-op-50-platform); user-select: none;
}
.custom-radio input { display: none; }
.radio-mark {
  width: 15px; height: 15px; border-radius: 50%; border: 2px solid var(--border-color); display: flex; align-items: center; justify-content: center; transition: 0.2s;
}
.custom-radio input:checked + .radio-mark { border-color: var(--color-primary); }
.custom-radio input:checked + .radio-mark::after {
  content: ''; width: 7px; height: 7px; border-radius: 50%; background: var(--color-primary);
}
.custom-radio input:checked ~ .radio-text { color: var(--text-platform); font-weight: 600; }

.filter-btn-group { display: grid; grid-template-columns: 1fr 2fr; gap: 8px; }
.filter-btn {
  height: 40px; padding: 0 18px; border: none; border-radius: 10px; font-size: var(--font-size-2xl); font-weight: 700; cursor: pointer; transition: filter 0.15s; text-transform: uppercase;
}
.filter-btn:hover { filter: brightness(1.15); }
.filter-btn--reset { background: transparent; border: 1px solid var(--border-color); color: var(--text-op-50-platform); }
.filter-btn--search { background: var(--btn-background); color: var(--btn-text); }

/* Lista */
.ab-list { display: flex; flex-direction: column; }
.ab-row-foot { margin-top: 2px; }
.ab-code { min-width: 0; overflow: hidden; text-overflow: ellipsis; font-family: monospace; font-size: var(--font-size-xl); color: var(--text-op-50-platform); }
.ab-paid { flex-shrink: 0; font-size: var(--font-size-xl); color: var(--text-op-50-platform); }
.ab-paid.is-paid { color: var(--text-platform); }
.ab-empty { display: flex; flex-direction: column; align-items: center; gap: 10px; padding: 32px 0; text-align: center; }

/* ===========================
   EMPTY / LOGIN STATE
=========================== */
.mb-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 24px 4px 8px;
  animation: fadeInUp 0.4s ease;
}
.mb-empty__art { margin-bottom: 14px; }
.mb-empty__title { max-width: 280px; font-size: var(--font-size-6xl); font-weight: 700; line-height: 1.3; color: var(--text-platform); margin-bottom: 6px; }
.mb-empty__sub { max-width: 270px; font-size: var(--font-size-3xl); line-height: 1.45; color: var(--text-op-50-platform); }
.mb-empty__cta {
  display: flex; align-items: center; justify-content: center; gap: 8px;
  width: 100%; height: 44px; margin-top: 20px; border: none; border-radius: 12px; cursor: pointer;
  background: var(--btn-background); color: var(--btn-text); font-size: var(--font-size-4xl); font-weight: 700;
  transition: transform 0.12s ease, filter 0.15s;
}
.mb-empty__cta:active { transform: scale(0.98); filter: brightness(0.95); }
.mb-empty__link {
  margin-top: 12px; padding: 6px 10px; border: none; background: transparent; cursor: pointer;
  color: var(--color-primary-light); font-size: var(--font-size-3xl); font-weight: 600;
}

/* ===========================
   BOTTOM SHEET MOBILE
=========================== */
.my-bets--sheet .section-header {
  padding: 0 0 12px;
}

.my-bets--sheet .section-label {
  font-size: var(--font-size-2xl);
  color: var(--text-platform);
}





.my-bets--sheet .skeleton-list { padding: 0; }
.my-bets--sheet .skel-bet { padding: 14px; margin: 0 0 8px; border-radius: 10px; }

/* ===========================
   CASHOUT REASON (disabled button)
=========================== */
.cashout-btn-reason { font-weight: 400; font-size: 0.85em; opacity: 0.85; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.cashout-reason { margin-top: 6px; font-size: var(--font-size-xl); text-align: center; color: var(--text-op-50-platform); line-height: 1.2; }

/* ===========================
   BETFREEZE — same shapes as the cashout hero, ice blue
=========================== */
.cashout-btn.cashout-btn--hero.freeze-hero {
  background: linear-gradient(135deg, #0ea5e9 0%, #0369a1 100%);
  color: #fff;
  box-shadow: 0 8px 20px -10px #0ea5e9, inset 0 1px 0 rgba(255, 255, 255, 0.18);
}
.freeze-hero .cashout-btn__label { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.freeze-hero__placed { opacity: 0.6; font-weight: 600; }
.freeze-hero__arrow { color: inherit !important; opacity: 0.8; margin: 0 2px; }

/* List: the teaser sits under the cashout, a step below it in weight */
.bet-row--freeze { margin-top: 6px; justify-content: flex-start; }
.bet-row--freeze .cashout-btn.freeze-hero { height: 36px; border-radius: 8px; font-size: var(--font-size-2xl); }
.bet-row--freeze .freeze-hero__go { padding: 3px 6px; }

/* Detail: availability chip, frozen legs, action under each open leg */
.td-freeze-available {
  display: flex; align-items: center; gap: 8px; margin: 0 0 10px; padding: 10px 12px; border-radius: 10px;
  border: 1px solid rgba(14, 165, 233, 0.45); background: rgba(14, 165, 233, 0.12);
  color: var(--text-platform); font-size: var(--font-size-2xl); font-weight: 600;
}
.td-freeze-available-icon { color: #38bdf8 !important; }
.badge-bet-freeze { display: inline-flex; align-items: center; justify-content: center; margin-left: 6px; color: #38bdf8; font-size: var(--font-size-3xl); line-height: 1; }
.badge-bet-freeze--leg { margin-left: 4px; }
/* Freeze voided with the whole bet: kept for the record, greyed out */
.badge-bet-freeze--overridden { color: var(--text-op-50-platform); opacity: 0.7; }
.td-leg-freeze-overridden { font-size: var(--font-size-xl); color: var(--text-op-50-platform); }
.bet-freeze-original-winning { opacity: 0.55; font-weight: 400; font-size: 0.85em; margin-right: 6px; color: inherit; }
/* Pre-freeze potential winning, on its own line under the figure */
/* Two-edge line: label flush left (under "Importo"), value flush right (under the winning) */
.bet-row--freeze-winning { justify-content: space-between; align-items: baseline; margin-top: -2px; }
.bet-freeze-before-value { flex-shrink: 0; font-size: var(--font-size-2xl); font-weight: 600; color: var(--text-op-50-platform); font-variant-numeric: tabular-nums; white-space: nowrap; }
.bet-money-amount, .bet-money-win { font-variant-numeric: tabular-nums; }
.bet-freeze-before { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: var(--font-size-xl); font-weight: 400; color: var(--text-op-50-platform); }
.bet-freeze-before s { margin-left: 2px; }
.td-stat-sub { display: block; }
.td-leg-odd.td-leg-odd--frozen { color: #38bdf8; white-space: nowrap; }
.td-leg-odd-placed { opacity: 0.55; font-weight: 600; font-size: 0.8em; margin-right: 3px; color: var(--text-op-50-platform); }

.td-freeze-area { padding: 0 14px 12px; }
.td-freeze-btn { width: 100%; height: 44px; border-radius: 10px; font-size: var(--font-size-3xl); }
.td-freeze-btn.cashout-btn--suspended { height: 38px; font-size: var(--font-size-2xl); justify-content: flex-start; padding: 0 12px; gap: 6px; }
.td-freeze-cost { display: flex; justify-content: space-between; margin-top: 6px; padding: 0 2px; font-size: var(--font-size-xl); color: var(--text-op-50-platform); }
.td-freeze-cost-value { color: #f59e0b; font-weight: 700; }

/* Confirm panel: the cashout panel look */
.td-freeze-panel {
  background: var(--sidebar); border: 1px solid rgba(14, 165, 233, 0.45); border-radius: 12px; padding: 14px;
  font-size: var(--font-size-2xl);
}
.td-freeze-panel-title { display: flex; align-items: center; gap: 6px; margin-bottom: 8px; font-weight: 800; text-transform: uppercase; color: #38bdf8; }
.td-freeze-panel-title .v-icon { color: inherit !important; }
.td-freeze-panel-row { display: flex; justify-content: space-between; gap: 12px; padding: 3px 0; color: var(--text-op-50-platform); }
.td-freeze-panel-row span:last-child { font-weight: 700; color: var(--text-platform); text-align: right; }
.td-freeze-panel-row--accent span { color: #38bdf8 !important; font-weight: 800; }
.td-freeze-panel-row--cost span:last-child { color: #f59e0b; }
.td-freeze-panel-fair { margin: -2px 0 6px; font-size: var(--font-size-xl); color: #4ade80; text-align: right; }
.td-freeze-help { opacity: 0.6; font-size: 0.75em; cursor: help; }
.td-freeze-panel-benefit { margin: 2px 0 6px; font-size: var(--font-size-xl); color: var(--text-op-50-platform); }
.td-freeze-panel-validity { display: flex; align-items: center; min-height: 16px; margin-top: 8px; font-size: var(--font-size-xl); font-weight: 700; color: #38bdf8; }
.td-freeze-panel-note { margin-top: 6px; font-size: var(--font-size-xl); line-height: 1.4; color: var(--text-op-50-platform); }
.td-freeze-panel-message { margin-top: 6px; font-weight: 700; }
.td-freeze-panel-message--warning { color: #f59e0b; }
.td-freeze-panel-message--error { color: #f87171; }
.td-freeze-panel-actions { margin-top: 12px; }
.cashout-confirm-btn.freeze-confirm-btn { background: linear-gradient(135deg, #0ea5e9 0%, #0369a1 100%); color: #fff; }

/* Outcome of a freeze, in the summary */
.td-freeze-notice {
  margin: 4px 0 0; padding: 8px 12px; border-radius: 10px; border-left: 3px solid #38bdf8;
  background: rgba(14, 165, 233, 0.12); color: var(--text-platform); font-size: var(--font-size-2xl); font-weight: 700;
}
.td-freeze-notice--warning { border-left-color: #f59e0b; background: rgba(245, 158, 11, 0.12); }
.td-freeze-notice--error { border-left-color: #ef4444; background: rgba(239, 68, 68, 0.12); }
</style>